import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./zonalheaddashboard.css";
import "./MonthlyReport.css";
import MonthlyReport from "./UserMonthlyReport";
import ProfilePage from "./ProfilePage";
import {
  FiBarChart2,
  FiCheckCircle,
  FiClock,
  FiPlus,
  FiLock,
  FiAward,
  FiTarget,
  FiUsers,
  FiSettings,
  FiTrendingUp,
  FiBell,
  FiFileText,
  FiChevronLeft,
  FiPhoneCall,
  FiCheckSquare,
  FiCalendar,
  FiUser,
} from "react-icons/fi";
import {
  Box,
  Alert,
  Button,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tooltip,
  Snackbar,
  Card,
  CardContent,
  Avatar,
  Chip,
} from "@mui/material";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  Tooltip as ChartTooltip,
} from "recharts";
import api from "../api/axiosConfig";

import SalesTracker from "./SalesTracker";
import ZonalHeadUserManagement from "./ZonalHeadUserManagement";
import defaultAvatar from "../images/download.png";
import logo1 from "../images/logo.jpg";

// --- Helper: Normalize user/member objects ---
const normalizeMemberObject = (member = {}) => {
  if (!member) return {};
  const m = { ...member };

  // Normalize IDs
  m.userId = m.userId ?? m.user_id ?? m.id ?? m.branchManagerId ?? m.branch_id;
  m.id = m.id ?? m.userId;

  // Normalize hierarchy
  m.reportsToId = m.reportsToId ?? m.reports_to_id ?? m.reportsTo ?? null;
  m.reportsToRole =
    m.reportsToRole ?? m.reports_to_role ?? m.reportsToRole ?? null;

  // Normalize names
  m.name =
    m.name ??
    m.branchManagerName ??
    m.branch_manager_name ??
    m.username ??
    m.userName ??
    m.username;
  m.username = m.username ?? m.user_name ?? m.userName ?? m.username;

  return m;
};

const toTitleCase = (str) => {
  if (!str) return "";
  const withSpaces = str.replace(/_/g, " ").replace(/([A-Z])/g, " $1");
  return withSpaces.replace(
    /\w\S*/g,
    (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
  );
};

const formatDateForInput = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date)) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatNotificationDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date)) return dateString;
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const formatMemberNameWithRole = (member = {}) => {
  const baseName = member.name || member.username || "Unknown";
  if (member.role === "branch_manager") return `${baseName} (BM)`;
  if (member.role === "team_lead") return `${baseName} (TL)`;
  if (member.role === "relationship_manager") return `${baseName} (RM)`;
  return baseName;
};

const combineUniqueMembers = (...groups) => {
  const seen = new Set();
  const combined = [];
  groups.forEach((group) => {
    if (!Array.isArray(group)) return;
    group.forEach((member) => {
      if (!member) return;
      const key = member.userId || member.id;
      if (!key || seen.has(key)) return;
      seen.add(key);
      combined.push(member);
    });
  });
  return combined;
};

const isUserOnLeaveOnDate = (user, dateStr) => {
  if (!user || !user.leaveStatus || !user.leaveStartDate) return false;
  const checkDate = new Date(dateStr + "T00:00:00");
  const startDate = new Date(user.leaveStartDate);
  const endDate = user.leaveEndDate ? new Date(user.leaveEndDate) : null;

  checkDate.setHours(0, 0, 0, 0);
  startDate.setHours(0, 0, 0, 0);
  if (endDate) endDate.setHours(0, 0, 0, 0);

  if (endDate) {
    return checkDate >= startDate && checkDate <= endDate;
  }
  return checkDate >= startDate;
};

// Function to check if a date is a government holiday in India
const isIndianGovernmentHoliday = (date, corporateHolidays = []) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();

  // Check against corporate holidays from API
  const holidayFromAPI = corporateHolidays.find((holiday) => {
    const holidayDate = new Date(holiday.date);
    return (
      holidayDate.getFullYear() === year &&
      holidayDate.getMonth() === month &&
      holidayDate.getDate() === day
    );
  });

  if (holidayFromAPI) {
    return {
      name: holidayFromAPI.name,
      type: holidayFromAPI.type,
    };
  }

  // Fallback to basic fixed holidays if API fails (for redundancy)
  const fixedHolidays = [
    { month: 0, day: 1, name: "New Year's Day" },
    { month: 0, day: 26, name: "Republic Day" },
    { month: 7, day: 15, name: "Independence Day" },
    { month: 9, day: 2, name: "Gandhi Jayanti" },
    { month: 11, day: 25, name: "Christmas" },
    { month: 4, day: 1, name: "Labour Day" },
  ];

  const fixedHoliday = fixedHolidays.find(
    (holiday) => holiday.month === month && holiday.day === day
  );
  if (fixedHoliday) {
    return {
      name: fixedHoliday.name,
      type: "national",
    };
  }

  return null;
};

// Moved outside the component for performance and to prevent stale closures.
const getMetric = (member = {}, key) => {
  if (!member || !key) return 0;

  const val = member[key];
  if (val === undefined || val === null) return 0;

  const num = Number(val);
  return Number.isNaN(num) ? 0 : num;
};

// ===== Added performance merge helper =====
const fetchPerformanceForMembers = async (members, start, end) => {
  const enriched = await Promise.all(
    members.map(async (m) => {
      try {
        const resp = await api.get(
          `/api/performance/summary?userId=${
            m.user_id || m.userId
          }&startDate=${start}&endDate=${end}`
        );
        return { ...m, ...resp.data };
      } catch (err) {
        return { ...m };
      }
    })
  );
  return enriched;
};
// ==========================================

const ZonalHeadDashboard = () => {
  const navigate = useNavigate();
  const { section, "*": restOfUrl } = useParams();
  const currentSection = section || "dashboard";

  let targetUserId = "me";
  let entryDateForTracker = formatDateForInput(new Date());

  if (section === "input-for" && restOfUrl) {
    const parts = restOfUrl.split("/");
    targetUserId = parts[0];
    if (parts[1]) entryDateForTracker = parts[1];
  } else if (section === "input" && restOfUrl) {
    entryDateForTracker = restOfUrl;
  }

  // Granular loading and error states for better UX
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isBranchDetailsLoading, setIsBranchDetailsLoading] = useState(false);
  const [isMyPerfLoading, setIsMyPerfLoading] = useState(false);
  const [isBranchPerfLoading, setIsBranchPerfLoading] = useState(false);
  const [isBranchCalendarLoading, setIsBranchCalendarLoading] = useState(false);

  // State variables
  const [dashboardData, setDashboardData] = useState(null);
  const [branchPerformanceData, setBranchPerformanceData] = useState([]);
  const [branchSubmissionHistory, setBranchSubmissionHistory] = useState([]);
  const [branchMembers, setBranchMembers] = useState([]);
  const [hierarchyInfo, setHierarchyInfo] = useState({}); // Store hierarchy structure info
  const [user, setUser] = useState({});
  const [todaysStatus, setTodaysStatus] = useState({});
  const [kpis, setKpis] = useState({});
  const [performanceTrend, setPerformanceTrend] = useState([]);
  const [submissionHistory, setSubmissionHistory] = useState({});
  const [notifications, setNotifications] = useState([]);
  const [pendingLeaveRequests, setPendingLeaveRequests] = useState([]);
  const [approvedLeaveRequests, setApprovedLeaveRequests] = useState([]);
  const [ownLeaveRequests, setOwnLeaveRequests] = useState([]);
  const [branchSubmissions, setBranchSubmissions] = useState([]);

  // Add monthly KPI data state for current and previous month comparison
  const [monthlyKpiData, setMonthlyKpiData] = useState({
    kpis: {},
    previousMonthKpis: {},
  });

  // Date range states
  const [branchDetailsStartDate, setBranchDetailsStartDate] = useState(
    () => new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
  );
  const [branchDetailsEndDate, setBranchDetailsEndDate] = useState(new Date());
  const [myPerformanceStartDate, setMyPerformanceStartDate] = useState(
    () => new Date(new Date().getFullYear(), new Date().getMonth(), 1)
  );
  const [myPerformanceEndDate, setMyPerformanceEndDate] = useState(
    () => new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)
  );
  const [branchPerformanceStartDate, setBranchPerformanceStartDate] = useState(
    () => new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
  );
  const [branchPerformanceEndDate, setBranchPerformanceEndDate] = useState(
    new Date()
  );
  const [branchCalendarStartDate, setBranchCalendarStartDate] = useState(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1)
  );
  const [branchCalendarEndDate, setBranchCalendarEndDate] = useState(
    new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)
  );

  // UI and control states
  const [dashboardSearchTerm, setDashboardSearchTerm] = useState("");
  const [userName] = useState(localStorage.getItem("name") || "User");
  const userRole = localStorage.getItem("userRole");
  const [selectedField1, setSelectedField1] = useState("dailyPoints");
  const [selectedField2, setSelectedField2] = useState("none");
  const [selectedLeaderboardMetric, setSelectedLeaderboardMetric] =
    useState("aggregatedPoints");
  const [selectedNotificationTab, setSelectedNotificationTab] =
    useState("pending-approvals");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Corporate holidays state
  const [corporateHolidays, setCorporateHolidays] = useState([]);
  const [holidaysLoading, setHolidaysLoading] = useState(false);

  // Dialog and form states
  const [leaveRequestDialogOpen, setLeaveRequestDialogOpen] = useState(false);
  const [leaveRejectDialogOpen, setLeaveRejectDialogOpen] = useState(false);
  const [selectedLeaveRequestId, setSelectedLeaveRequestId] = useState(null);
  const [leaveRejectionReason, setLeaveRejectionReason] = useState("");
  const [leaveRevocationReason, setLeaveRevocationReason] = useState("");
  const [leaveRevokeDialogOpen, setLeaveRevokeDialogOpen] = useState(false);
  const [selectedLeaveRevokeRequestId, setSelectedLeaveRevokeRequestId] =
    useState(null);
  const [leaveRequestForm, setLeaveRequestForm] = useState({
    reason: "",
    start_date: "",
    end_date: "",
  });
  const [isOnLeave, setIsOnLeave] = useState(false);
  const [leaveDurationDays, setLeaveDurationDays] = useState(0);
  const [leaveEndDate, setLeaveEndDate] = useState(null);
  const [selectedPerfMetric, setSelectedPerfMetric] = useState("totalPoints");
  const [chartData, setChartData] = useState([]);
  const [showMissingEntryModal, setShowMissingEntryModal] = useState(false);
  const [showBranchManagersModal, setShowBranchManagersModal] = useState(false);
  const [showChartBranchManagersModal, setShowChartBranchManagersModal] =
    useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalSubmittedList, setModalSubmittedList] = useState([]);
  const [modalMissingList, setModalMissingList] = useState([]);
  const [modalOnLeaveList, setModalOnLeaveList] = useState([]);

  // Hierarchical calendar state
  const [calendarLevel, setCalendarLevel] = useState("zonal"); // 'zonal' | 'branch_manager' | 'team_lead'
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUserName, setSelectedUserName] = useState("");
  const [currentLevelUsers, setCurrentLevelUsers] = useState([]);
  const [calendarTargetUsers, setCalendarTargetUsers] = useState([]); // Users for calendar percentage calculation
  const [navigationHistory, setNavigationHistory] = useState([]); // Track navigation path: [{level, userId, userName}]
  const [showCurrentLevelUsersModal, setShowCurrentLevelUsersModal] =
    useState(false);

  // Hierarchical chart state
  const [chartLevel, setChartLevel] = useState("zonal"); // 'zonal' | 'branch_manager' | 'team_lead'
  const [chartSelectedUserId, setChartSelectedUserId] = useState(null);
  const [chartSelectedUserName, setChartSelectedUserName] = useState("");
  const [chartNavigationHistory, setChartNavigationHistory] = useState([]); // Track chart navigation path: [{level, userId, userName}]
  const [chartCurrentLevelUsers, setChartCurrentLevelUsers] = useState([]);
  const [showChartCurrentLevelUsersModal, setShowChartCurrentLevelUsersModal] =
    useState(false);
  const [chartViewMode, setChartViewMode] = useState("chart"); // 'chart' | 'calendar'
  const [chartViewingDirectZonalRMs, setChartViewingDirectZonalRMs] =
    useState(false);
  const [chartDirectZonalRMs, setChartDirectZonalRMs] = useState([]);
  const [isFetchingChartDirectZonalRMs, setIsFetchingChartDirectZonalRMs] =
    useState(false);
  const [
    chartViewingBranchManagerDirectRMs,
    setChartViewingBranchManagerDirectRMs,
  ] = useState(false);
  const [chartBranchManagerDirectRMs, setChartBranchManagerDirectRMs] =
    useState([]);

  // Hierarchical table state for Branch Managers Overview
  const [tableLevel, setTableLevel] = useState("zonal"); // 'zonal' | 'branch_manager' | 'team_lead' | 'relationship_manager'
  const [tableSelectedUserId, setTableSelectedUserId] = useState(null);
  const [tableSelectedUserName, setTableSelectedUserName] = useState("");
  const [tableNavigationHistory, setTableNavigationHistory] = useState([]); // Track table navigation path: [{level, userId, userName}]
  const [tableCurrentLevelUsers, setTableCurrentLevelUsers] = useState([]);
  const [showTableBranchManagersModal, setShowTableBranchManagersModal] =
    useState(false);
  const [showDirectRMsModal, setShowDirectRMsModal] = useState(false);
  const [directRMManagerName, setDirectRMManagerName] = useState("");
  const [directRMsForModal, setDirectRMsForModal] = useState([]);
  const [viewingDirectZonalRMs, setViewingDirectZonalRMs] = useState(false);
  const [directZonalRMs, setDirectZonalRMs] = useState([]);
  const [isFetchingDirectZonalRMs, setIsFetchingDirectZonalRMs] =
    useState(false);
  const [viewingBranchManagerDirectRMs, setViewingBranchManagerDirectRMs] =
    useState(false);
  const [branchManagerDirectRMs, setBranchManagerDirectRMs] = useState([]);
  const [selectedTeamLeadRecordId, setSelectedTeamLeadRecordId] =
    useState(null);
  const [noTeamLeadsDialogOpen, setNoTeamLeadsDialogOpen] = useState(false);
  const [noTeamLeadsBranchName, setNoTeamLeadsBranchName] = useState("");
  const [noDirectRMsDialogOpen, setNoDirectRMsDialogOpen] = useState(false);
  const [noDirectRMsBranchName, setNoDirectRMsBranchName] = useState("");

  const zonalBranchManagers = useMemo(
    () => branchMembers.filter((member) => member.role === "branch_manager"),
    [branchMembers]
  );

  const directZonalTeamLeads = useMemo(
    () =>
      branchMembers.filter(
        (member) =>
          member.role === "team_lead" &&
          (!member.reportsToRole || member.reportsToRole === "zonal_head")
      ),
    [branchMembers]
  );

  const directZonalRelationshipManagers = useMemo(
    () =>
      branchMembers.filter(
        (member) =>
          member.role === "relationship_manager" &&
          (!member.reportsToRole || member.reportsToRole === "zonal_head")
      ),
    [branchMembers]
  );

  const normalizeRelationshipManager = (rm = {}) => ({
    ...rm,
    role: rm.role || "relationship_manager",
    userId: rm.userId || rm.id,
    name: rm.name || rm.username || rm.fullName || "Unknown",
  });

  const buildUniqueRelationshipManagers = (list = []) => {
    if (!Array.isArray(list) || !list.length) return [];
    return list
      .map((rm) => normalizeRelationshipManager(rm))
      .filter((rm) => rm.userId || rm.id)
      .filter((rm, index, self) => {
        const key = rm.userId || rm.id;
        return index === self.findIndex((m) => (m.userId || m.id) === key);
      });
  };

  const getDirectZonalRMsFromHierarchy = () => {
    const rmCandidates = branchMembers.filter(
      (member) => member.role === "relationship_manager"
    );
    return buildUniqueRelationshipManagers(rmCandidates);
  };

  const getBranchManagerRMsFromLocal = (branchManager = {}) => {
    if (!branchManager || !Array.isArray(branchManager.members)) return [];
    const collected = [];
    branchManager.members.forEach((member) => {
      if (member.role === "relationship_manager") {
        collected.push(member);
      } else if (member.role === "team_lead" && Array.isArray(member.members)) {
        member.members
          .filter((rm) => rm.role === "relationship_manager")
          .forEach((rm) => collected.push(rm));
      }
    });
    return buildUniqueRelationshipManagers(collected);
  };

  const openDirectRMCalendarView = (rmList, notifyIfEmpty = true) => {
    if (!rmList.length) {
      if (notifyIfEmpty) {
        setSnackbar({
          open: true,
          message: "No direct Relationship Managers found.",
          severity: "info",
        });
      }
      return false;
    }
    setDirectZonalRMs(rmList);
    setViewingDirectZonalRMs(true);
    setShowBranchManagersModal(false);
    setSelectedUserId(null);
    setSelectedUserName("");
    setNavigationHistory([]);
    setCalendarLevel("zonal");
    setSelectedTeamLeadRecordId(null);
    setViewingBranchManagerDirectRMs(false);
    setBranchManagerDirectRMs([]);
    return true;
  };

  const openDirectRMChartView = (rmList, notifyIfEmpty = true) => {
    if (!rmList.length) {
      if (notifyIfEmpty) {
        setSnackbar({
          open: true,
          message: "No direct Relationship Managers found.",
          severity: "info",
        });
      }
      return false;
    }
    setChartDirectZonalRMs(rmList);
    setChartViewingDirectZonalRMs(true);
    setChartLevel("zonal");
    setChartSelectedUserId(null);
    setChartSelectedUserName("");
    setChartNavigationHistory([]);
    setShowChartBranchManagersModal(false);
    return true;
  };

  const chartFieldOptions = [
    { value: "dailyPoints", label: "Daily Points" },
    { value: "telephoneSalesCalls", label: "Telephone Sales Calls" },
    { value: "faceToFace", label: "Face to Face" },
    { value: "appointmentMade", label: "Appointment Made" },
    { value: "appointmentKept", label: "Appointment Kept" },
    { value: "factFind", label: "Fact Find" },
    { value: "serviceReviews", label: "Service Reviews" },
    { value: "socialCalls", label: "Social Calls" },
    { value: "highIncomeWealthy", label: "High Income / Wealthy" },
    { value: "referrals", label: "Referrals" },
    { value: "closingInterview", label: "Closing Interview" },
    { value: "salesClose", label: "Sales Close" },
    { value: "none", label: "None" },
  ];
  const leaderboardFieldOptions = [
    { value: "aggregatedPoints", label: "Aggregated Points" },
    ...chartFieldOptions.filter((option) => option.value !== "dailyPoints"),
  ];
  const performanceChartMetrics = [
    { value: "totalPoints", label: "Total Points" },
    { value: "telephoneSalesCalls", label: "Telephone Sales Calls" },
    { value: "faceToFace", label: "Face to Face" },
    { value: "appointmentMade", label: "Appointment Made" },
    { value: "appointmentKept", label: "Appointment Kept" },
    { value: "factFind", label: "Fact Find" },
    { value: "serviceReviews", label: "Service Reviews" },
    { value: "socialCalls", label: "Social Calls" },
    { value: "highIncomeWealthy", label: "High Income / Wealthy" },
    { value: "referrals", label: "Referrals" },
    { value: "closingInterview", label: "Closing Interview" },
    { value: "salesClose", label: "Sales Close" },
  ];

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar({ ...snackbar, open: false });
  };

  const handleMemberSelect = (leader) => {

    setShowBranchManagersModal(false);
    setViewingDirectZonalRMs(false);
    setViewingBranchManagerDirectRMs(false);
    setBranchManagerDirectRMs([]);

    const role = leader.role;
    const id = leader.id || leader.userId;
    const name = leader.name || leader.username || "User";
    const teamLeadRecordId =
      leader.teamLeadRecordId ||
      leader.teamLeadId ||
      leader.team_lead_id ||
      leader.teamleadid ||
      null;

    if (role === "branch_manager") {
      const hasTeamLeads =
        Array.isArray(leader.members) &&
        leader.members.some((member) => member.role === "team_lead");
      if (!hasTeamLeads) {
        setNoTeamLeadsBranchName(name);
        setNoTeamLeadsDialogOpen(true);
        return;
      }
      navigateToLevel("branch_manager", id, name);
    } else if (role === "team_lead") {
      navigateToLevel("team_lead", id, name, {
        teamLeadRecordId: teamLeadRecordId || id,
      });
    } else {
      navigateToLevel("relationship_manager", id, name);
    }
  };

  const handleChartMemberSelect = (leader) => {

    setShowChartBranchManagersModal(false);
    setViewingDirectZonalRMs(false);
    setChartViewingDirectZonalRMs(false);
    setChartDirectZonalRMs([]);
    resetChartBranchManagerDirectRMView();

    const role = leader.role;
    const id = leader.id || leader.userId;

    setChartSelectedUserId(id);
    setChartSelectedUserName(leader.name);

    // Set chart level only
    if (role === "branch_manager") {
      setChartLevel("branch_manager");
    } else if (role === "team_lead") {
      setChartLevel("team_lead");
    } else {
      setChartLevel("relationship_manager");
    }
  };

  const handleViewDirectRMs = (leader = {}) => {
    const membersArray = Array.isArray(leader.members) ? leader.members : [];
    const directRMs = membersArray.filter(
      (member) => member.role === "relationship_manager"
    );
    const uniqueDirectRMs = buildUniqueRelationshipManagers(directRMs);

    setDirectRMsForModal(uniqueDirectRMs);
    setDirectRMManagerName(leader.name || leader.username || "Branch Manager");
    setShowDirectRMsModal(true);
  };

  const handleChartViewBranchManagerDirectRMs = async (leader = {}) => {
    const id = leader.id || leader.userId;
    const name = leader.name || leader.username || "Branch Manager";

    if (!id) {
      setSnackbar({
        open: true,
        message: "Unable to load this branch manager. Missing identifier.",
        severity: "error",
      });
      return;
    }

    try {
      setIsBranchPerfLoading(true);
      const response = await api.get("/api/users/rms-by-branch-manager", {
        params: { branchManagerId: id, directOnly: true },
      });
      let rmList = buildUniqueRelationshipManagers(response.data || []);

      // Trust the API response - if empty, there are legitimately no direct RMs
      if (!rmList.length) {
        setSnackbar({
          open: true,
          message:
            `${name} does not have any Relationship Managers reporting directly to them.`,
          severity: "info",
        });
        setChartViewingBranchManagerDirectRMs(false);
        setChartBranchManagerDirectRMs([]);
        return;
      }

      setChartBranchManagerDirectRMs(rmList);
      setChartViewingBranchManagerDirectRMs(true);

      navigateChartToLevel("branch_manager", id, name, {
        preserveBranchManagerDirectRMs: true,
      });
      setChartData(transformPerformanceData(rmList));
      setShowChartBranchManagersModal(false);
    } catch (error) {
      console.error(
        "Failed to load direct Relationship Managers for chart:",
        error
      );
      setSnackbar({
        open: true,
        message:
          "Failed to load Relationship Managers for this Branch Manager. Please try again.",
        severity: "error",
      });
    } finally {
      setIsBranchPerfLoading(false);
    }
  };

  const handleDirectTeamLeadSelect = async (leader = {}) => {
    const id = leader.id || leader.userId;
    const name = leader.name || leader.username || "Team Lead";

    if (!id) {
      setSnackbar({
        open: true,
        message: "Unable to open this team lead. Missing identifier.",
        severity: "error",
      });
      return;
    }

    setShowBranchManagersModal(false);
    setViewingDirectZonalRMs(false);
    setViewingBranchManagerDirectRMs(false);
    setBranchManagerDirectRMs([]);
    const teamLeadRecordId =
      leader.teamLeadRecordId ||
      leader.teamLeadId ||
      leader.team_lead_id ||
      leader.teamleadid ||
      leader.id ||
      id;
    navigateToLevel("team_lead", id, name, {
      teamLeadRecordId,
    });

    setIsBranchCalendarLoading(true);
    try {
      const [teamMembersRes, submissionsRes] = await Promise.all([
        api.get(`/api/users/rms-by-team-lead/${id}`),
        api.get(
          `/api/users/branch-detailed-submission-history?startDate=${formatDateForInput(
            branchCalendarStartDate
          )}&endDate=${formatDateForInput(branchCalendarEndDate)}&teamLeadId=${
            teamLeadRecordId || id
          }`
        ),
      ]);

      setCurrentLevelUsers(teamMembersRes.data || []);

      const transformedData = (submissionsRes.data || []).map((entry) => ({
        userId: entry.userId,
        date: entry.date,
        points: entry.points,
      }));
      setBranchSubmissionHistory(transformedData);
    } catch (error) {
      console.error("Failed to load team lead calendar data:", error);
      setSnackbar({
        open: true,
        message:
          "Failed to load Relationship Managers for this Team Lead. Please try again.",
        severity: "error",
      });
    } finally {
      setIsBranchCalendarLoading(false);
    }
  };

  const handleBranchManagerDirectRMsSelect = async (leader = {}) => {
    const id = leader.id || leader.userId;
    const name = leader.name || leader.username || "Branch Manager";

    if (!id) {
      setSnackbar({
        open: true,
        message: "Unable to open this branch manager. Missing identifier.",
        severity: "error",
      });
      return;
    }

    try {
      setIsBranchCalendarLoading(true);
      const [rmResponse, submissionsRes] = await Promise.all([
        api.get("/api/users/rms-by-branch-manager", {
          params: { branchManagerId: id, directOnly: true },
        }),
        api.get(
          `/api/users/branch-detailed-submission-history?startDate=${formatDateForInput(
            branchCalendarStartDate
          )}&endDate=${formatDateForInput(
            branchCalendarEndDate
          )}&branchManagerId=${id}&directRMs=true`
        ),
      ]);

      let rmList = buildUniqueRelationshipManagers(rmResponse.data || []);
      
      // Trust the API response - if empty, there are legitimately no direct RMs
      if (!rmList.length) {
        setSnackbar({
          open: true,
          message:
            `${name} does not have any Relationship Managers reporting directly to them.`,
          severity: "info",
        });
        setViewingBranchManagerDirectRMs(false);
        setBranchManagerDirectRMs([]);
        return;
      }

      if (!(calendarLevel === "branch_manager" && selectedUserId === id)) {
        navigateToLevel("branch_manager", id, name);
      } else {
        setSelectedUserName(name);
      }

      setShowBranchManagersModal(false);
      setViewingDirectZonalRMs(false);
      setViewingBranchManagerDirectRMs(true);
      setBranchManagerDirectRMs(rmList);
      setCurrentLevelUsers(rmList);
      setSelectedTeamLeadRecordId(null);

      const transformedHistory = (submissionsRes.data || []).map((entry) => ({
        userId: entry.userId,
        date: entry.date,
        points: entry.points,
      }));
      setBranchSubmissionHistory(transformedHistory);
    } catch (error) {
      console.error("Failed to load direct RMs under branch manager:", error);
      const fallbackFromHierarchy = getBranchManagerRMsFromLocal(leader);
      if (!fallbackFromHierarchy.length) {
        setNoDirectRMsBranchName(name);
        setNoDirectRMsDialogOpen(true);
        setViewingBranchManagerDirectRMs(false);
        setBranchManagerDirectRMs([]);
        setBranchSubmissionHistory([]);
        setSnackbar({
          open: true,
          message:
            "Failed to load Relationship Managers for this Branch Manager. Please try again.",
          severity: "error",
        });
      } else {
        setBranchManagerDirectRMs(fallbackFromHierarchy);
        setViewingBranchManagerDirectRMs(true);
        setCurrentLevelUsers(fallbackFromHierarchy);
        setBranchSubmissionHistory([]);
        setShowBranchManagersModal(false);
        setSnackbar({
          open: true,
          message: "Showing locally cached Relationship Manager data.",
          severity: "warning",
        });
      }
    } finally {
      setIsBranchCalendarLoading(false);
    }
  };

  const handleViewZonalDirectRMs = async () => {
    try {
      setIsFetchingDirectZonalRMs(true);
      const response = await api.get(
        "/api/users/relationship-managers-by-zonal-head",
        {
          params: { range: 30 },
        }
      );
      const uniqueDirectRMs = buildUniqueRelationshipManagers(
        response.data || []
      );

      if (openDirectRMCalendarView(uniqueDirectRMs, false)) {
        return;
      }

      const fallbackList = getDirectZonalRMsFromHierarchy();
      openDirectRMCalendarView(fallbackList);
    } catch (error) {
      console.error("Failed to load direct relationship managers:", error);
      const fallbackList = getDirectZonalRMsFromHierarchy();
      if (!openDirectRMCalendarView(fallbackList, false)) {
        setSnackbar({
          open: true,
          message: "Failed to load direct Relationship Managers.",
          severity: "error",
        });
      } else {
        setSnackbar({
          open: true,
          message: "Showing cached Relationship Manager data.",
          severity: "warning",
        });
      }
    } finally {
      setIsFetchingDirectZonalRMs(false);
    }
  };

  const handleChartViewZonalDirectRMs = async () => {
    try {
      setIsFetchingChartDirectZonalRMs(true);
      const response = await api.get(
        "/api/users/relationship-managers-by-zonal-head",
        { params: { range: 30 } }
      );
      const uniqueDirectRMs = buildUniqueRelationshipManagers(
        response.data || []
      );
      if (openDirectRMChartView(uniqueDirectRMs, false)) return;
      const fallbackList = getDirectZonalRMsFromHierarchy();
      openDirectRMChartView(fallbackList);
    } catch (error) {
      console.error("Failed to load direct RM data for chart:", error);
      const fallbackList = getDirectZonalRMsFromHierarchy();
      if (!openDirectRMChartView(fallbackList, false)) {
        setSnackbar({
          open: true,
          message: "Failed to load direct Relationship Managers.",
          severity: "error",
        });
      } else {
        setSnackbar({
          open: true,
          message: "Showing cached Relationship Manager data.",
          severity: "warning",
        });
      }
    } finally {
      setIsFetchingChartDirectZonalRMs(false);
    }
  };

  const resetChartDirectRMView = () => {
    setChartViewingDirectZonalRMs(false);
    setChartDirectZonalRMs([]);
  };

  const resetChartBranchManagerDirectRMView = () => {
    setChartViewingBranchManagerDirectRMs(false);
    setChartBranchManagerDirectRMs([]);
  };

  const resetDirectZonalRMView = () => {
    setViewingDirectZonalRMs(false);
    setDirectZonalRMs([]);
  };

  const fetchLeaveAndNotifications = useCallback(async () => {
    await Promise.allSettled([
      api
        .get("/api/users/me/my-leave-requests")
        .then(async (res) => setOwnLeaveRequests(res.data || [])),
      api
        .get("/api/users/subordinate-leave-requests")
        .then(async (res) => setPendingLeaveRequests(res.data || [])),
      api
        .get("/api/users/zonal-head-leave-requests/approved")
        .then(async (res) => setApprovedLeaveRequests(res.data || [])),
      api
        .get("/api/users/me/notifications")
        .then(async (res) => setNotifications(res.data || [])),
    ]);
  }, []);

  const reloadDataForSection = useCallback(() => {
    // Reloading ensures all dashboard widgets reflect the newest submission.
    window.location.reload();
  }, []);

  // Fetch data that doesn't depend on date ranges, called once on mount.
  const fetchInitialStaticData = useCallback(async () => {
    setIsInitialLoading(true);
    await Promise.allSettled([
      api
        .get(`/api/users/me?t=${new Date().getTime()}`)
        .then(async (res) => {
          const userData = res.data;
          if (userData) {
            const photoPath = userData.profilePhoto || userData.profile_photo;
            let fullUrl = defaultAvatar;
            if (photoPath) {
              if (
                photoPath.startsWith("http://") ||
                photoPath.startsWith("https://")
              ) {
                fullUrl = photoPath;
              } else {
                const serverOrigin = new URL(api.defaults.baseURL).origin;
                const cleanPath = photoPath.replace(/^\/+/, "");

                fullUrl = `${serverOrigin}/${cleanPath}`;
              }
            }
            setUser({ ...userData, profile_photo: fullUrl });
            localStorage.setItem("name", userData.name || userData.username);
            localStorage.setItem("userId", userData.userId);
          }
        })
        .catch((err) => {
          console.error("Failed to fetch user profile for header:", err);
          setUser((prev) => ({ ...prev, profile_photo: defaultAvatar }));
        }),
      api
        .get("/api/users/me/leave-status")
        .then(async (res) => {
          setIsOnLeave(res.data.leaveStatus);
          if (res.data.leaveStatus && res.data.leaveStartDate) {
            setLeaveDurationDays(
              Math.max(
                1,
                Math.floor(
                  (new Date() - new Date(res.data.leaveStartDate)) / 86400000
                ) + 1
              )
            );
            setLeaveEndDate(res.data.leaveEndDate || null);
          }
        })
        .catch(() => {}),
      fetchLeaveAndNotifications(),
      api
        .get("/api/users/me/branch-submissions")
        .then(async (res) => setBranchSubmissions(res.data || []))
        .catch((err) =>
          console.error("Failed to load branch submissions", err)
        ),
    ]);
    setIsInitialLoading(false);
  }, [fetchLeaveAndNotifications]);

  // Corporate holidays - using fallback holidays only (no API fetch)
  useEffect(() => {
    setCorporateHolidays([]);
  }, []);

  // Initial data load on component mount
  useEffect(() => {
    const fetchInitialData = async () => {
      setIsInitialLoading(true);
      try {
        await fetchInitialStaticData();
        if (["dashboard", "input"].includes(currentSection)) {
          // Fetch the static KPIs for the current month
          const today = new Date();
          const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
          const lastDay = new Date(
            today.getFullYear(),
            today.getMonth() + 1,
            0
          );
          const kpiRes = await api.get(
            `/api/users/me/dashboard?startDate=${formatDateForInput(
              firstDay
            )}&endDate=${formatDateForInput(lastDay)}`
          );
          if (kpiRes.data) {
            setMonthlyKpiData({
              kpis: kpiRes.data.kpis,
              previousMonthKpis: kpiRes.data.previousMonthKpis,
            });
          }
        }
      } catch (e) {
        console.error("Error on initial fetch", e);
      } finally {
        setIsInitialLoading(false);
      }
    };
    fetchInitialData();
  }, [fetchInitialStaticData, currentSection]);
  useEffect(() => {
    setIsBranchDetailsLoading(true);
    const startDate = formatDateForInput(branchDetailsStartDate);
    const endDate = formatDateForInput(branchDetailsEndDate);

    // Use different API based on user role
    const apiUrl =
      userRole === "zonal_head"
        ? "/api/users/zonal-head-hierarchy"
        : "/api/users/me/subordinates";

    api
      .get(apiUrl, {
        params: {
          startDate,
          endDate,
        },
      })
      .then(async (perfRes) => {
        const perfData = perfRes.data || [];

        // For branch managers, use the data as-is since my-subordinates returns properly formatted data
        if (userRole === "branch_manager") {
          setBranchMembers(perfData);
          setHierarchyInfo({
            hasBranchManagers: false,
            hasDirectTeamLeads: perfData.some((m) => m.role === "team_lead"),
            hasDirectRelationshipManagers: perfData.some(
              (m) => m.role === "relationship_manager"
            ),
          });
        } else {
          // For zonal heads, use the hierarchical structure from API
          setBranchMembers(perfData.topLevelTeams || []);
          setHierarchyInfo(perfData.hierarchy || {});
        }
      })

      .catch((err) => {
        console.error("Failed to load data:", err);
        setError("Could not load data.");
      })
      .finally(() => setIsBranchDetailsLoading(false));
  }, [branchDetailsStartDate, branchDetailsEndDate, userRole]);

  // Fetch My Performance data when its date range changes
  useEffect(() => {
    setIsMyPerfLoading(true);
    api
      .get(
        `/api/users/me/dashboard?startDate=${formatDateForInput(
          myPerformanceStartDate
        )}&endDate=${formatDateForInput(myPerformanceEndDate)}`
      )
      .then(async (res) => {
        setDashboardData(res.data);
        setKpis(res.data.kpis || {});
        setPerformanceTrend(res.data.performanceTrend || []);
        setSubmissionHistory(res.data.submissionHistory || {});
        setTodaysStatus(res.data.todaysStatus || {});
      })
      .catch((err) => {
        console.error("Failed to load My Performance data:", err);
        setError(
          "Failed to load your performance data. Please check your connection and try again."
        );
      })
      .finally(() => setIsMyPerfLoading(false));
  }, [myPerformanceStartDate, myPerformanceEndDate]);

  // Fetch Branch Performance data when its date range changes
  useEffect(() => {
    setIsBranchPerfLoading(true);
    api
      .get(
        `/api/users/branch-performance?startDate=${formatDateForInput(
          branchPerformanceStartDate
        )}&endDate=${formatDateForInput(branchPerformanceEndDate)}`
      )
      .then(async (res) => setBranchPerformanceData(res.data || []))
      .catch((err) => {
        console.error("Failed to load Branch Performance data:", err);
        setError(
          "Failed to load branch performance data. Please check your permissions and try again."
        );
      })
      .finally(() => setIsBranchPerfLoading(false));
  }, [branchPerformanceStartDate, branchPerformanceEndDate]);

  // Fetch Branch Submission Calendar data when its date range changes
  useEffect(() => {
    setIsBranchCalendarLoading(true);
    let queryParams = `startDate=${formatDateForInput(
      branchCalendarStartDate
    )}&endDate=${formatDateForInput(branchCalendarEndDate)}`;

    // Add level-specific parameters
    if (calendarLevel === "branch_manager" && selectedUserId) {
      queryParams += viewingBranchManagerDirectRMs
        ? `&branchManagerId=${selectedUserId}&directRMs=true`
        : `&branchManagerId=${selectedUserId}`;
    } else if (
      calendarLevel === "team_lead" &&
      (selectedTeamLeadRecordId || selectedUserId)
    ) {
      const teamLeadParam = selectedTeamLeadRecordId || selectedUserId || "";
      queryParams += `&teamLeadId=${teamLeadParam}`;
    } else if (calendarLevel === "relationship_manager" && selectedUserId) {
      queryParams += `&userId=${selectedUserId}`;
    }
    if (viewingDirectZonalRMs) {
      queryParams += `&directRMs=true`;
    }

    api
      .get(`/api/users/branch-detailed-submission-history?${queryParams}`)
      .then(async (res) => {
        const transformedData = (res.data || []).map((entry) => ({
          userId: entry.userId,
          date: entry.date,
          points: entry.points,
        }));
        setBranchSubmissionHistory(transformedData);
      })
      .catch((err) => {
        console.error("Failed to load Branch Submission History:", err);
        setError(
          "Failed to load submission history data. Please check your permissions and try again."
        );
      })
      .finally(() => setIsBranchCalendarLoading(false));
  }, [
    branchCalendarStartDate,
    branchCalendarEndDate,
    calendarLevel,
    selectedUserId,
    selectedTeamLeadRecordId,
    viewingDirectZonalRMs,
    viewingBranchManagerDirectRMs,
  ]);

  // Fetch submission history when calendar level or date range changes
  useEffect(() => {
    fetchCurrentLevelSubmissionHistory();
  }, [
    calendarLevel,
    selectedUserId,
    selectedTeamLeadRecordId,
    branchCalendarStartDate,
    branchCalendarEndDate,
    viewingBranchManagerDirectRMs,
    viewingDirectZonalRMs,
  ]);

  const transformPerformanceData = useCallback((data) => {
    if (!data || !Array.isArray(data)) return [];
    return data.map((item) => {
      const normalizedName = item.name || item.username || "Unknown";
      return {
        userId: item.userId,
        name: normalizedName,
        displayName: formatMemberNameWithRole(item) || normalizedName,
        role: item.role,
        totalPoints: getMetric(item, "aggregatedPoints"),
        telephoneSalesCalls: getMetric(item, "telephoneSalesCalls"),
        faceToFace: getMetric(item, "faceToFace"),
        appointmentMade: getMetric(item, "appointmentMade"),
        appointmentKept: getMetric(item, "appointmentKept"),
        factFind: getMetric(item, "factFind"),
        serviceReviews: getMetric(item, "serviceReviews"),
        socialCalls: getMetric(item, "socialCalls"),
        highIncomeWealthy: getMetric(item, "highIncomeWealthy"),
        referrals: getMetric(item, "referrals"),
        closingInterview: getMetric(item, "closingInterview"),
        salesClose: getMetric(item, "salesClose"),
        new_leads: getMetric(item, "newLeads"),
      };
    });
  }, []);

  // Dynamically set chart data for zonal level based on flexible hierarchy
  useEffect(() => {
    if (chartLevel === "zonal") {
      if (chartViewingDirectZonalRMs && chartDirectZonalRMs.length > 0) {
        setChartData(transformPerformanceData(chartDirectZonalRMs));
        return;
      }
      let dataToShow = [];

      if (hierarchyInfo.hasBranchManagers) {
        // Zonal → BM (+ possible TLs or RMs)
        dataToShow = branchPerformanceData.filter(
          (m) =>
            m.role === "branch_manager" ||
            (m.role === "team_lead" && m.reportsToRole === "zonal_head") ||
            (m.role === "relationship_manager" &&
              m.reportsToRole === "zonal_head")
        );
      } else if (hierarchyInfo.hasDirectTeamLeads) {
        // Zonal → TL → RM
        dataToShow = branchPerformanceData.filter(
          (m) =>
            m.role === "team_lead" ||
            (m.role === "relationship_manager" &&
              m.reportsToRole === "zonal_head")
        );
      } else if (hierarchyInfo.hasDirectRelationshipManagers) {
        // Zonal → RM directly
        dataToShow = branchPerformanceData.filter(
          (m) => m.role === "relationship_manager"
        );
      } else {
        // Fallback - show all subordinates if hierarchy unknown
        dataToShow = branchPerformanceData;
      }

      setChartData(transformPerformanceData(dataToShow));
    }
  }, [
    chartLevel,
    branchPerformanceData,
    transformPerformanceData,
    hierarchyInfo,
    chartViewingDirectZonalRMs,
    chartDirectZonalRMs,
  ]);

  // Fetch chart data when chart level changes
  useEffect(() => {
    if (chartLevel !== "zonal") {
      if (
        chartLevel === "branch_manager" &&
        chartViewingBranchManagerDirectRMs
      ) {
        setChartData(transformPerformanceData(chartBranchManagerDirectRMs));
        return;
      }

      const fetchChartLevelData = async () => {
        try {
          if (!chartSelectedUserId) {
            console.warn("???,? No user selected for chart fetch");
            return;
          }

          let endpoint = "";
          const dateParams = `?startDate=${formatDateForInput(
            branchPerformanceStartDate
          )}&endDate=${formatDateForInput(branchPerformanceEndDate)}`;

          if (chartLevel === "branch_manager") {
            // dY"1 Fetch team leads under this branch manager
            endpoint = `/api/users/team-leads-by-branch-manager?branchManagerId=${chartSelectedUserId}${dateParams.replace(
              "?",
              "&"
            )}`;
          } else if (chartLevel === "team_lead") {
            // dY"1 Fetch relationship managers under this team lead (TL does NOT submit entries)
            endpoint = `/api/users/rms-by-team-lead/${chartSelectedUserId}${dateParams}`;
          } else if (chartLevel === "relationship_manager") {
            // dY"1 Fetch specific RM performance
            endpoint = `/api/users/relationship-manager-performance?rmId=${chartSelectedUserId}${dateParams.replace(
              "?",
              "&"
            )}`;
          } else {
            console.warn("???,? Unknown chart level:", chartLevel);
            return;
          }

          if (endpoint) {
            const response = await api.get(endpoint);
            const data = response.data || [];

            // ?. Update chart data - transform ensures only performance metrics are used
            setChartData(transformPerformanceData(data));
          }
        } catch (error) {
          console.error("??O Failed to fetch chart level data:", error);

          // dY"? Fallback to local filtering if API fails (only for team_lead level should care about reporting structure)
          let fallbackData = [];
          if (chartLevel === "branch_manager") {
            fallbackData = branchPerformanceData.filter(
              (m) =>
                m.reportsToId === chartSelectedUserId &&
                m.reportsToRole === "branch_manager" &&
                m.role === "team_lead"
            );
          } else if (chartLevel === "team_lead") {
            // dY"1 Only RMs under the TL, not the TL himself since TLs don't have performance data
            fallbackData = branchPerformanceData.filter(
              (m) =>
                m.reportsToId === chartSelectedUserId &&
                m.reportsToRole === "team_lead" &&
                m.role === "relationship_manager"
            );
          } else if (chartLevel === "relationship_manager") {
            fallbackData = branchPerformanceData.filter(
              (m) => m.userId === chartSelectedUserId
            );
          }
          setChartData(transformPerformanceData(fallbackData));
        }
      };

      fetchChartLevelData();
    }
  }, [
    chartLevel,
    chartSelectedUserId,
    branchPerformanceStartDate,
    branchPerformanceEndDate,
    branchPerformanceData,
    transformPerformanceData,
    chartViewingBranchManagerDirectRMs,
    chartBranchManagerDirectRMs,
  ]);

  // --- END: Refactored Data Fetching ---

  // Optimized lookup for branch members
  const branchMembersMap = useMemo(() => {
    if (!branchMembers || branchMembers.length === 0) return new Map();
    return new Map(branchMembers.map((member) => [member.userId, member]));
  }, [branchMembers]);

  // Filter pending leave requests to exclude those already approved
  const filteredPendingLeaveRequests = useMemo(() => {
    const approvedIds = new Set(
      approvedLeaveRequests.map((req) => req.leaveRequestId)
    );
    return pendingLeaveRequests.filter(
      (request) => !approvedIds.has(request.leaveRequestId)
    );
  }, [pendingLeaveRequests, approvedLeaveRequests]);

  // Navigation functions for hierarchical calendar
  const navigateToLevel = useCallback(
    (level, userId = null, userName = "", metadata = {}) => {
      const newHistoryEntry = { level, userId, userName, ...metadata };
      setNavigationHistory((prev) => [...prev, newHistoryEntry]);
      setCalendarLevel(level);
      setSelectedUserId(userId);
      setSelectedUserName(userName);
      if (level === "team_lead") {
        setSelectedTeamLeadRecordId(metadata.teamLeadRecordId || null);
      } else {
        setSelectedTeamLeadRecordId(null);
      }
    },
    []
  );

  const navigateBack = useCallback(() => {
    if (navigationHistory.length > 0) {
      const newHistory = [...navigationHistory];
      newHistory.pop(); // Remove current level
      setNavigationHistory(newHistory);

      if (newHistory.length === 0) {
        // Back to zonal level
        setCalendarLevel("zonal");
        setSelectedUserId(null);
        setSelectedUserName("");
        setCurrentLevelUsers([]);
        setSelectedTeamLeadRecordId(null);
        setViewingBranchManagerDirectRMs(false);
        setBranchManagerDirectRMs([]);
      } else {
        // Back to previous level
        const prevEntry = newHistory[newHistory.length - 1];
        setCalendarLevel(prevEntry.level);
        setSelectedUserId(prevEntry.userId);
        setSelectedUserName(prevEntry.userName);
        if (prevEntry.level === "team_lead") {
          setSelectedTeamLeadRecordId(prevEntry.teamLeadRecordId || null);
          setViewingBranchManagerDirectRMs(false);
          setBranchManagerDirectRMs([]);
        } else if (prevEntry.level === "branch_manager") {
          setSelectedTeamLeadRecordId(null);
        } else {
          setSelectedTeamLeadRecordId(null);
          setViewingBranchManagerDirectRMs(false);
          setBranchManagerDirectRMs([]);
        }
      }
    }
  }, [navigationHistory]);

  // Navigation functions for hierarchical chart
  const navigateChartToLevel = useCallback(
    (level, userId = null, userName = "", options = {}) => {
      const newHistoryEntry = { level, userId, userName };
      setChartNavigationHistory((prev) => [...prev, newHistoryEntry]);
      setChartLevel(level);
      setChartSelectedUserId(userId);
      setChartSelectedUserName(userName);
      if (level !== "zonal") {
        setChartViewingDirectZonalRMs(false);
        setChartDirectZonalRMs([]);
      }
      if (
        !(options?.preserveBranchManagerDirectRMs && level === "branch_manager")
      ) {
        setChartViewingBranchManagerDirectRMs(false);
        setChartBranchManagerDirectRMs([]);
      }
    },
    []
  );

  const navigateChartBack = useCallback(() => {
    if (chartNavigationHistory.length > 0) {
      const newHistory = [...chartNavigationHistory];
      newHistory.pop(); // Remove current level
      setChartNavigationHistory(newHistory);
      setChartViewingBranchManagerDirectRMs(false);
      setChartBranchManagerDirectRMs([]);

      if (newHistory.length === 0) {
        // Back to zonal level
        setChartLevel("zonal");
        setChartSelectedUserId(null);
        setChartSelectedUserName("");
        setChartCurrentLevelUsers([]);
        setChartViewingDirectZonalRMs(false);
        setChartDirectZonalRMs([]);
      } else {
        // Back to previous level
        const prevEntry = newHistory[newHistory.length - 1];
        setChartLevel(prevEntry.level);
        setChartSelectedUserId(prevEntry.userId);
        setChartSelectedUserName(prevEntry.userName);
      }
    }
  }, [chartNavigationHistory]);

  // Navigation functions for hierarchical table
  const navigateTableToLevel = useCallback(
    (level, userId = null, userName = "") => {
      const newHistoryEntry = { level, userId, userName };
      setTableNavigationHistory((prev) => [...prev, newHistoryEntry]);
      setTableLevel(level);
      setTableSelectedUserId(userId);
      setTableSelectedUserName(userName);
    },
    []
  );

  const navigateTableBack = useCallback(() => {
    if (tableNavigationHistory.length > 0) {
      const newHistory = [...tableNavigationHistory];
      newHistory.pop(); // Remove current level
      setTableNavigationHistory(newHistory);

      if (newHistory.length === 0) {
        // Back to zonal level
        setTableLevel("zonal");
        setTableSelectedUserId(null);
        setTableSelectedUserName("");
        setTableCurrentLevelUsers([]);
      } else {
        // Back to previous level
        const prevEntry = newHistory[newHistory.length - 1];
        setTableLevel(prevEntry.level);
        setTableSelectedUserId(prevEntry.userId);
        setTableSelectedUserName(prevEntry.userName);
      }
    }
  }, [tableNavigationHistory]);

  // Fetch current level users based on calendar level
  const fetchCurrentLevelUsers = useCallback(async () => {
    if (calendarLevel === "zonal") {
      const topLevelMembers = combineUniqueMembers(
        zonalBranchManagers,
        directZonalTeamLeads
      );
      setCurrentLevelUsers(topLevelMembers);
      return;
    }

    if (calendarLevel === "branch_manager" && viewingBranchManagerDirectRMs) {
      setCurrentLevelUsers(branchManagerDirectRMs);
      return;
    }

    try {
      let response;
      if (calendarLevel === "branch_manager") {
        response = await api.get(
          `/api/users/team-leads-by-branch-manager?branchManagerId=${selectedUserId}`
        );
      } else if (calendarLevel === "team_lead") {
        // ✅ Only RMs under the TL - TL himself should NOT be included since he doesn't submit entries
        response = await api.get(
          `/api/users/rms-by-team-lead/${selectedUserId}`
        );
      }

      if (response?.data) {
        setCurrentLevelUsers(response.data);
      } else {
        setCurrentLevelUsers([]);
      }
    } catch (error) {
      console.error("Failed to fetch current level users:", error);
      setCurrentLevelUsers([]);
      setSnackbar({
        open: true,
        message: `Failed to load team members for ${
          calendarLevel === "branch_manager" ? "branch manager" : "team lead"
        }`,
        severity: "error",
      });
    }
  }, [
    calendarLevel,
    selectedUserId,
    branchMembers,
    viewingBranchManagerDirectRMs,
    branchManagerDirectRMs,
    zonalBranchManagers,
    directZonalTeamLeads,
  ]);

  // Fetch submission history for current level
  const fetchCurrentLevelSubmissionHistory = useCallback(async () => {
    if (!selectedUserId || calendarLevel === "zonal") return;

    setIsBranchCalendarLoading(true);
    try {
      let queryParams = `startDate=${formatDateForInput(
        branchCalendarStartDate
      )}&endDate=${formatDateForInput(branchCalendarEndDate)}`;

      // Add level-specific parameters
      if (calendarLevel === "branch_manager") {
        queryParams += viewingBranchManagerDirectRMs
          ? `&branchManagerId=${selectedUserId}&directRMs=true`
          : `&branchManagerId=${selectedUserId}`;
      } else if (calendarLevel === "team_lead") {
        const teamLeadParam = selectedTeamLeadRecordId || selectedUserId || "";
        queryParams += `&teamLeadId=${teamLeadParam}`;
      }

      const response = await api.get(
        `/api/users/branch-detailed-submission-history?${queryParams}`
      );
      const transformedData = (response.data || []).map((entry) => ({
        userId: entry.userId,
        date: entry.date,
        points: entry.points,
      }));
      setBranchSubmissionHistory(transformedData);
    } catch (error) {
      setBranchSubmissionHistory([]);
      setSnackbar({
        open: true,
        message: `Failed to load submission history for ${
          calendarLevel === "branch_manager" ? "branch manager" : "team lead"
        }`,
        severity: "error",
      });
    } finally {
      setIsBranchCalendarLoading(false);
    }
  }, [
    calendarLevel,
    selectedUserId,
    selectedTeamLeadRecordId,
    branchCalendarStartDate,
    branchCalendarEndDate,
  ]);

  // Fetch chart current level users based on chart level
  const fetchChartCurrentLevelUsers = useCallback(async () => {
    if (chartLevel === "zonal") {
      // Already have branch managers from branchMembers
      setChartCurrentLevelUsers(
        branchMembers.filter((m) => m.role === "branch_manager")
      );
      return;
    }

    if (chartLevel === "branch_manager" && chartViewingBranchManagerDirectRMs) {
      setChartCurrentLevelUsers(chartBranchManagerDirectRMs);
      return;
    }

    try {
      let response;
      if (chartLevel === "branch_manager") {
        response = await api.get(
          `/api/users/team-leads-by-branch-manager?branchManagerId=${chartSelectedUserId}`
        );
      } else if (chartLevel === "team_lead") {
        response = await api.get(
          `/api/users/rms-by-team-lead/${chartSelectedUserId}`
        );
      }

      if (response?.data) {
        setChartCurrentLevelUsers(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch chart current level users:", error);
      setSnackbar({
        open: true,
        message: "Failed to load team members for chart",
        severity: "error",
      });
    }
  }, [
    chartLevel,
    chartSelectedUserId,
    branchMembers,
    chartViewingBranchManagerDirectRMs,
    chartBranchManagerDirectRMs,
  ]);

  // Fetch current level users when calendar level changes
  useEffect(() => {
    fetchCurrentLevelUsers();
  }, [
    calendarLevel,
    selectedUserId,
    branchMembers,
    viewingBranchManagerDirectRMs,
    branchManagerDirectRMs,
  ]);

  // Set calendar target users based on calendar level
  useEffect(() => {
    if (calendarLevel === "zonal") {
      if (viewingDirectZonalRMs && directZonalRMs.length > 0) {
        setCalendarTargetUsers(directZonalRMs);
        return;
      }
      let targetUsers = [];

      if (hierarchyInfo.hasBranchManagers) {
        // Zonal → BM (+ possible TLs or RMs)
        targetUsers = combineUniqueMembers(
          zonalBranchManagers,
          directZonalTeamLeads,
          directZonalRelationshipManagers
        );
      } else if (hierarchyInfo.hasDirectTeamLeads) {
        // Zonal → TL → RM
        targetUsers = combineUniqueMembers(
          directZonalTeamLeads,
          directZonalRelationshipManagers
        );
      } else if (hierarchyInfo.hasDirectRelationshipManagers) {
        // Zonal → RM directly
        targetUsers = [...directZonalRelationshipManagers];
      } else {
        // Fallback to all if hierarchy unknown
        targetUsers = branchMembers;
      }

      setCalendarTargetUsers(targetUsers);
    } else {
      setCalendarTargetUsers(currentLevelUsers);
    }
  }, [
    calendarLevel,
    currentLevelUsers,
    branchMembers,
    hierarchyInfo,
    viewingDirectZonalRMs,
    directZonalRMs,
    zonalBranchManagers,
    directZonalTeamLeads,
    directZonalRelationshipManagers,
  ]);

  // Fetch chart current level users when chart level changes
  useEffect(() => {
    fetchChartCurrentLevelUsers();
  }, [
    chartLevel,
    chartSelectedUserId,
    branchMembers,
    chartViewingBranchManagerDirectRMs,
    chartBranchManagerDirectRMs,
  ]);

  // Fetch table data when table level changes
  useEffect(() => {
    if (tableLevel !== "zonal") {
      const fetchTableLevelData = async () => {
        try {
          if (!tableSelectedUserId) {
            console.warn("⚠️ No user selected for table fetch");
            return;
          }

          let endpoint = "";
          const dateParams = `?startDate=${formatDateForInput(
            branchDetailsStartDate
          )}&endDate=${formatDateForInput(branchDetailsEndDate)}`;

          if (tableLevel === "branch_manager") {
            // Fetch team leads under this branch manager
            endpoint = `/api/users/team-leads-by-branch-manager?branchManagerId=${tableSelectedUserId}${dateParams.replace(
              "?",
              "&"
            )}`;
          } else if (tableLevel === "team_lead") {
            // Fetch relationship managers under this team lead
            endpoint = `/api/users/rms-by-team-lead/${tableSelectedUserId}${dateParams}`;
          } else if (tableLevel === "relationship_manager") {
            // Fetch specific RM performance
            endpoint = `/api/users/relationship-manager-performance?rmId=${tableSelectedUserId}${dateParams.replace(
              "?",
              "&"
            )}`;
          } else {
            console.warn("⚠️ Unknown table level:", tableLevel);
            return;
          }

          if (endpoint) {
            const response = await api.get(endpoint);
            const data = response.data || [];
            console.log(`Table level ${tableLevel} data:`, data);

            // Update branchMembers with the new data for table display
            setBranchMembers(transformPerformanceData(data));
          }
        } catch (error) {
          console.error("❌ Failed to fetch table level data:", error);

          // Fallback to local filtering if API fails
          let fallbackData = [];
          if (tableLevel === "branch_manager") {
            fallbackData = branchPerformanceData.filter(
              (m) =>
                m.reportsToId === tableSelectedUserId &&
                m.reportsToRole === "branch_manager"
            );
          } else if (tableLevel === "team_lead") {
            fallbackData = branchPerformanceData.filter(
              (m) =>
                m.reportsToId === tableSelectedUserId &&
                m.reportsToRole === "team_lead"
            );
          } else if (tableLevel === "relationship_manager") {
            fallbackData = branchPerformanceData.filter(
              (m) =>
                m.userId === tableSelectedUserId ||
                m.reportsToId === tableSelectedUserId
            );
          }
          setBranchMembers(transformPerformanceData(fallbackData));
        }
      };

      fetchTableLevelData();
    } else {
      // When back to zonal level, refetch the original hierarchy data
      setIsBranchDetailsLoading(true);
      const startDate = formatDateForInput(branchDetailsStartDate);
      const endDate = formatDateForInput(branchDetailsEndDate);
      api
        .get(
          `/api/users/zonal-head-hierarchy?startDate=${startDate}&endDate=${endDate}`
        )
        .then(async (res) => {
          const data = res.data || {};
          const topLevel =
            data.topLevelTeams && data.topLevelTeams.length > 0
              ? data.topLevelTeams
              : data.members && data.members.length > 0
              ? data.members
              : data.branchManagers || [];
          setBranchMembers(topLevel);
          setHierarchyInfo(data.hierarchy || {});
        })
        .catch((err) => {
          console.error("Failed to load hierarchy data:", err);
          setError("Could not load hierarchy data.");
        })
        .finally(() => setIsBranchDetailsLoading(false));
    }
  }, [
    tableLevel,
    tableSelectedUserId,
    branchDetailsStartDate,
    branchDetailsEndDate,
    branchPerformanceData,
    transformPerformanceData,
  ]);

  const handleCalendarDateClick = (dateStr) => {
    const submittedEntriesOnDate = branchSubmissionHistory.filter(
      (entry) => entry.date === dateStr
    );
    const submittedUserIds = new Set(
      submittedEntriesOnDate.map((entry) => Number(entry.userId))
    );

    let modalTitleText = "";
    let targetGroup = [];
    const hasDirectZonalTeamLeads = directZonalTeamLeads.length > 0;

    if (calendarLevel === "zonal") {
      if (viewingDirectZonalRMs) {
        modalTitleText = `Direct Relationship Manager Submissions for ${formatNotificationDate(
          dateStr
        )}`;
        targetGroup = directZonalRMs;
      } else if (hierarchyInfo.hasBranchManagers) {
        modalTitleText = hasDirectZonalTeamLeads
          ? `Branch Manager & Team Lead Submissions for ${formatNotificationDate(
              dateStr
            )}`
          : `Branch Manager Submissions for ${formatNotificationDate(dateStr)}`;
      } else if (hierarchyInfo.hasDirectTeamLeads) {
        modalTitleText = `Team Lead Submissions for ${formatNotificationDate(
          dateStr
        )}`;
      } else if (hierarchyInfo.hasDirectRelationshipManagers) {
        modalTitleText = `Relationship Manager Submissions for ${formatNotificationDate(
          dateStr
        )}`;
      } else {
        modalTitleText = `Executive Submissions for ${formatNotificationDate(
          dateStr
        )}`;
      }
      targetGroup = targetGroup.length ? targetGroup : calendarTargetUsers;
    } else if (calendarLevel === "branch_manager") {
      if (viewingBranchManagerDirectRMs) {
        modalTitleText = `${selectedUserName} Branch Managers Direct Relationship Managers Performance for ${formatNotificationDate(
          dateStr
        )}`;
        targetGroup = branchManagerDirectRMs;
      } else {
        modalTitleText = `${selectedUserName} Team Lead Submissions for ${formatNotificationDate(
          dateStr
        )}`;
        targetGroup = currentLevelUsers;
      }
    } else if (calendarLevel === "team_lead") {
      modalTitleText = `${selectedUserName} RM Submissions for ${formatNotificationDate(dateStr)}`;
      targetGroup = currentLevelUsers;
    }


    setModalTitle(modalTitleText);

    const submitted = [];
    const missing = [];
    const onLeave = [];

    targetGroup.forEach((member) => {
      const labeledMember = {
        ...member,
        displayName: formatMemberNameWithRole(member),
      };
      const memberKey = Number(labeledMember.userId || labeledMember.id);
      if (isUserOnLeaveOnDate(labeledMember, dateStr)) {
        onLeave.push(labeledMember);
      } else if (submittedUserIds.has(memberKey)) {
        const submission = submittedEntriesOnDate.find(
          (s) => Number(s.userId) === memberKey
        );
        submitted.push({
          ...labeledMember,
          points: submission?.points || 0,
        });
      } else {
        missing.push(labeledMember);
      }
    });

    setModalSubmittedList(submitted);
    setModalMissingList(missing);
    setModalOnLeaveList(onLeave);
    setShowMissingEntryModal(true);
  };

  // Reset viewingBranchManagerDirectRMs when navigating to different calendar levels
  const resetViewingFlags = () => {
    setViewingBranchManagerDirectRMs(false);
    setViewingDirectZonalRMs(false);
  };

  useEffect(() => {
    if (calendarLevel !== "branch_manager") {
      setViewingBranchManagerDirectRMs(false);
    }
  }, [calendarLevel]);

  // Handlers now call targeted fetch functions
  const handleApproveLeaveRequest = async (requestId) => {
    try {
      await api.put(`/api/users/leave-request/${requestId}/approve`);
      setSnackbar({
        open: true,
        message: "Leave request approved!",
        severity: "success",
      });
      await fetchLeaveAndNotifications(); // Refresh only relevant data
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Error approving leave request.",
        severity: "error",
      });
    }
  };

  const handleOpenLeaveRejectDialog = (requestId) => {
    setSelectedLeaveRequestId(requestId);
    setLeaveRejectDialogOpen(true);
  };

  const handleRejectLeaveRequest = async () => {
    if (!leaveRejectionReason.trim()) {
      setSnackbar({
        open: true,
        message: "Please provide a reason for rejection.",
        severity: "warning",
      });
      return;
    }
    try {
      await api.put(
        `/api/users/leave-request/${selectedLeaveRequestId}/reject`,
        { rejectionReason: leaveRejectionReason }
      );
      setSnackbar({
        open: true,
        message: "Leave request rejected.",
        severity: "success",
      });
      setLeaveRejectDialogOpen(false);
      setLeaveRejectionReason("");
      await fetchLeaveAndNotifications();
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Error rejecting leave request.",
        severity: "error",
      });
    }
  };

  const handleSubmitLeaveRequest = async () => {
    if (!leaveRequestForm.reason.trim() || !leaveRequestForm.start_date) {
      setSnackbar({
        open: true,
        message: "Reason and start date are required.",
        severity: "warning",
      });
      return;
    }
    try {
      await api.post("/api/users/me/leave-request", leaveRequestForm);
      setSnackbar({
        open: true,
        message: "Leave request submitted!",
        severity: "success",
      });
      setLeaveRequestDialogOpen(false);
      setLeaveRequestForm({ reason: "", start_date: "", end_date: "" });
      await fetchLeaveAndNotifications();
    } catch (error) {
      setSnackbar({
        open: true,
        message:
          error.response?.data?.message || "Error submitting leave request.",
        severity: "error",
      });
    }
  };

  const handleOpenLeaveRevokeDialog = (requestId) => {
    setSelectedLeaveRevokeRequestId(requestId);
    setLeaveRevokeDialogOpen(true);
  };

  const handleRevokeLeaveRequest = async () => {
    if (!leaveRevocationReason.trim()) {
      setSnackbar({
        open: true,
        message: "Please provide a reason for revocation.",
        severity: "warning",
      });
      return;
    }
    try {
      await api.put(
        `/api/users/leave-request/${selectedLeaveRevokeRequestId}/revoke`,
        { revocationReason: leaveRevocationReason }
      );
      setSnackbar({
        open: true,
        message: "Leave request revoked.",
        severity: "success",
      });
      setLeaveRevokeDialogOpen(false);
      setLeaveRevocationReason("");
      await fetchLeaveAndNotifications();
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Error revoking leave request.",
        severity: "error",
      });
    }
  };

  const getTopPerformers = useMemo(() => {
    if (!branchMembers.length) return [];
    const topLevelMembers = branchMembers.filter(
      (member) =>
        member.role ===
        (hierarchyInfo.hasBranchManagers
          ? "branch_manager"
          : hierarchyInfo.hasDirectTeamLeads
          ? "team_lead"
          : "relationship_manager")
    );
    return [...topLevelMembers].sort(
      (a, b) =>
        getMetric(b, selectedLeaderboardMetric) -
        getMetric(a, selectedLeaderboardMetric)
    );
  }, [branchMembers, selectedLeaderboardMetric, hierarchyInfo]);

  const generateCalendarDays = useCallback(
    (historyData) => {
      const daysArray = [];
      if (
        !myPerformanceStartDate ||
        isNaN(myPerformanceStartDate) ||
        !myPerformanceEndDate ||
        isNaN(myPerformanceEndDate)
      )
        return [];
      const start = new Date(
        myPerformanceStartDate.getFullYear(),
        myPerformanceStartDate.getMonth(),
        1
      );
      const finalDate = new Date(myPerformanceEndDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const blankDays = start.getDay() === 0 ? 6 : start.getDay() - 1;
      for (let i = 0; i < blankDays; i++) daysArray.push({ key: `blank-${i}` });
      let loopDate = new Date(start);
      while (loopDate <= finalDate) {
        const dateStr = formatDateForInput(loopDate);
        const isSunday = loopDate.getDay() === 0;
        const holidayObj = isIndianGovernmentHoliday(
          loopDate,
          corporateHolidays
        );
        const isHoliday = !!holidayObj;
        let status = null;
        let holidayName = null;
        if (loopDate > today) {
          if (isHoliday) {
            status = "holiday";
            holidayName = holidayObj.name;
          } else {
            status = "future";
          }
        } else if (isHoliday) {
          status = "holiday";
          holidayName = holidayObj.name;
        } else if (isSunday) {
          status = "sunday";
        } else if (historyData && historyData[dateStr]) {
          status = "submitted";
        } else {
          status = "missing";
        }

        daysArray.push({
          key: dateStr,
          day: loopDate.getDate(),
          status,
          dateStr,
          holidayName,
        });
        loopDate.setDate(loopDate.getDate() + 1);
      }
      return daysArray;
    },
    [myPerformanceStartDate, myPerformanceEndDate, corporateHolidays]
  );

  const generateBranchOrTeamCalendarDays = useCallback(
    (historyData, memberList) => {
      const daysArray = [];
      if (
        !branchCalendarStartDate ||
        !branchCalendarEndDate ||
        isNaN(branchCalendarStartDate) ||
        isNaN(branchCalendarEndDate)
      )
        return [];
      let loopDate = new Date(
        branchCalendarStartDate.getFullYear(),
        branchCalendarStartDate.getMonth(),
        1
      );
      const finalDate = new Date(branchCalendarEndDate);
      const todayZero = new Date();
      todayZero.setHours(0, 0, 0, 0);

      const blankDays = loopDate.getDay() === 0 ? 6 : loopDate.getDay() - 1;
      for (let i = 0; i < blankDays; i++) daysArray.push({ key: `blank-${i}` });

      // Use currentLevelUsers for hierarchical navigation, fallback to branch managers for zonal level
      const targetGroup = memberList || currentLevelUsers;

      const submissionCounts = {};
      historyData.forEach((entry) => {
        const date = entry.date;
        if (!submissionCounts[date]) submissionCounts[date] = new Set();
        submissionCounts[date].add(Number(entry.userId));
      });


      while (loopDate <= finalDate) {
        const dateStr = formatDateForInput(loopDate);
        const isSunday = loopDate.getDay() === 0;
        const holidayObj = isIndianGovernmentHoliday(
          loopDate,
          corporateHolidays
        );
        const isHoliday = !!holidayObj;

        const activeMembersOnDate = targetGroup.filter(
          (m) => !isUserOnLeaveOnDate(m, dateStr)
        );
        const onLeaveCount = targetGroup.length - activeMembersOnDate.length;

        const submittedOnDate = activeMembersOnDate.filter((m) =>
          submissionCounts[dateStr]?.has(Number(m.userId || m.id))
        );

        // console.log(`📅 ${dateStr}: ${activeMembersOnDate.length} active, ${submittedOnDate.length} submitted`);

        let status = "future";
        let percentage = 0;
        let holidayName = null;

        if (loopDate <= todayZero) {
          // Calculate percentage for all days including Sundays and holidays
          percentage =
            activeMembersOnDate.length > 0
              ? Math.round(
                  (submittedOnDate.length / activeMembersOnDate.length) * 100
                )
              : 0;

          if (isHoliday) {
            status = "holiday";
            holidayName = holidayObj.name;
          } else if (
            onLeaveCount > 0 &&
            onLeaveCount >= activeMembersOnDate.length
          ) {
            status = "on_leave";
          } else if (isSunday && percentage === 0) {
            // Only show "sunday" status if no submissions on Sunday
            status = "sunday";
          } else {
            status = percentage > 0 ? "submitted" : "missing";
          }
        } else if (isHoliday) {
          status = "holiday";
          holidayName = holidayObj.name;
        }

        daysArray.push({
          key: dateStr,
          day: loopDate.getDate(),
          percentage,
          dateStr,
          status,
          holidayName,
        });
        loopDate.setDate(loopDate.getDate() + 1);
      }
      return daysArray;
    },
    [
      branchCalendarStartDate,
      branchCalendarEndDate,
      currentLevelUsers,
      corporateHolidays,
    ]
  );

  const calendarDays = useMemo(
    () => generateCalendarDays(submissionHistory),
    [generateCalendarDays, submissionHistory]
  );

  const branchPerformanceCalendarDays = useMemo(
    () =>
      generateBranchOrTeamCalendarDays(
        branchSubmissionHistory,
        calendarLevel === "zonal" ? calendarTargetUsers : currentLevelUsers
      ),
    [
      branchSubmissionHistory,
      calendarLevel,
      calendarTargetUsers,
      currentLevelUsers,
    ]
  );

  if (isInitialLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading Dashboard...</Typography>
      </Box>
    );
  }

  if (error && currentSection === "dashboard") {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Alert severity="error">{error}</Alert>
        <Button
          onClick={() => window.location.reload()}
          sx={{ mt: 2 }}
          variant="outlined"
        >
          Try Again
        </Button>
      </Box>
    );
  }

  const monthlyTargetPoints = 1000;
  const monthlyTargetProgress =
    kpis && monthlyTargetPoints > 0
      ? Math.round((kpis.aggregatedPoints / monthlyTargetPoints) * 100)
      : 0;
  const conversionRate =
    kpis && kpis.totalCalls > 0
      ? Math.round((kpis.salesClosed / kpis.totalCalls) * 100)
      : 0;
  const daysInMonth = new Date(
    new Date().getFullYear(),
    new Date().getMonth() + 1,
    0
  ).getDate();
  const avgDailyCalls =
    kpis && daysInMonth > 0 ? (kpis.totalCalls / daysInMonth).toFixed(1) : 0;
  const avgSalesClose =
    kpis && daysInMonth > 0 ? (kpis.salesClosed / daysInMonth).toFixed(1) : 0;
  const dashboardTitle = `${toTitleCase(userRole)} Dashboard`;
  const userRoleDisplay = toTitleCase(userRole);

  return (
    <div>
      {isOnLeave && (
        <div
          style={{
            background: "linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)",
            color: "white",
            padding: "20px 32px",
            textAlign: "center",
            fontSize: "16px",
            fontWeight: "500",
            position: "sticky",
            top: 0,
            zIndex: 10001,
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
            borderBottom: "3px solid #ff6b35",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "16px",
            minHeight: "60px",
          }}
        >
          <div
            style={{
              backgroundColor: "rgba(255, 107, 53, 0.2)",
              borderRadius: "50%",
              padding: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "2px solid rgba(255, 255, 255, 0.3)",
            }}
          >
            <FiClock style={{ fontSize: "24px", color: "#ff6b35" }} />
          </div>
          <div style={{ textAlign: "left" }}>
            <div
              style={{
                fontSize: "18px",
                fontWeight: "600",
                marginBottom: "4px",
              }}
            >
              Currently on Leave
            </div>
            <div style={{ fontSize: "14px", opacity: "0.9" }}>
              Day {leaveDurationDays} of your leave period{" "}
              {leaveEndDate &&
                `• Returns: ${new Date(leaveEndDate).toLocaleDateString(
                  "en-US",
                  {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  }
                )}`}
            </div>
          </div>
        </div>
      )}
      <header className="dashboard-header">
        <div className="header-container">
          <div className="header-left">
            <div className="logo-container">
              <div className="logo">
                {" "}
                <img src={logo1} alt="Logo" />{" "}
              </div>
              <span className="logo-text">Aionion Capital Markets</span>
            </div>
            <div className="header-title-group">
              <h1>{dashboardTitle}</h1>
              <span className="performance-tag">Performance Tracking</span>
            </div>
          </div>
          <div className="header-right">
            <nav className="nav-tabs">
              <button
                className={`nav-tab ${
                  currentSection === "dashboard" ? "active" : ""
                }`}
                onClick={() => navigate("/zonal-head-dashboard/dashboard")}
              >
                <FiBarChart2 /> Dashboard
              </button>
              <button
                className="nav-tab"
                onClick={() => navigate("/zonal-head-dashboard/input")}
              >
                <FiPlus /> Input
              </button>
              <button
                className={`nav-tab ${
                  currentSection === "monthly-report" ? "active" : ""
                }`}
                onClick={() => navigate("/zonal-head-dashboard/monthly-report")}
              >
                <FiFileText /> Monthly Report
              </button>
              <button
                className={`nav-tab ${
                  currentSection === "user-management" ? "active" : ""
                }`}
                onClick={() =>
                  navigate("/zonal-head-dashboard/user-management")
                }
              >
                <FiUsers /> User Management
              </button>
              <button
                className={`nav-tab ${
                  currentSection === "notifications" ? "active" : ""
                }`}
                onClick={() => navigate("/zonal-head-dashboard/notifications")}
              >
                <FiBell /> Notifications
              </button>
              <button
                className={`nav-tab ${
                  currentSection === "settings" ? "active" : ""
                }`}
                onClick={() => navigate("/zonal-head-dashboard/settings")}
              >
                <FiSettings /> Settings
              </button>
            </nav>
            <div className="user-profile">
              <img
                src={user.profile_photo || defaultAvatar}
                alt="Profile"
                className="profile-picture"
                onError={(e) => {
                  e.target.src = defaultAvatar;
                }}
              />
              <div className="user-info">
                <span className="user-name">{user.name || userName}</span>
                <span className="user-role">{userRoleDisplay}</span>
              </div>
            </div>
          </div>
        </div>
      </header>
      <main className="main-content">
        {(currentSection === "input" || currentSection === "input-for") && (
          <SalesTracker
            dashboardData={dashboardData}
            onBackClick={() => navigate("/zonal-head-dashboard/dashboard")}
            onEntrySubmit={reloadDataForSection}
            userId={targetUserId}
            entryDate={entryDateForTracker}
          />
        )}
        {currentSection === "dashboard" && (
          <div className="dashboard-grid">
            <section className="card todays-status-card">
              <div className="card-header">
                <h2>Today's Status</h2>
                <div className="live-indicator">
                  {(() => {
                    const date = new Date();
                    const day = date.getDate();
                    const month = date.toLocaleString("en-GB", {
                      month: "short",
                    });
                    const year = date.getFullYear();
                    return `${day} ${month},${year}`;
                  })()}
                </div>
              </div>
              <div className="status-boxes">
                {todaysStatus.submitted ? (
                  <div className="status-box submitted">
                    <div className="status-content">
                      <h3>Submitted</h3> <p>Entry for today is complete</p>
                      <span>
                        <FiClock /> Last updated at{" "}
                        {todaysStatus.submissionTime &&
                          new Date(
                            todaysStatus.submissionTime
                          ).toLocaleTimeString("en-US", {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          })}
                      </span>
                    </div>
                    <div className="status-icon-wrapper green">
                      <FiCheckCircle />
                    </div>
                  </div>
                ) : (
                  <div className="status-box not-submitted">
                    <div className="status-content">
                      <h3>Not Submitted</h3> <p>No entry for today yet</p>
                      <span>Click Quick Entry to submit.</span>
                    </div>
                    <div className="status-icon-wrapper grey">
                      <FiClock />
                    </div>
                  </div>
                )}
                <Tooltip
                  title={
                    <div style={{ fontSize: "14px", lineHeight: "1.6" }}>
                      <div>Avg Daily Calls Made: {monthlyKpiData?.kpis?.avgDailyCalls || "0.0"}</div>
                      <div>Avg Sales Closed: {monthlyKpiData?.kpis?.avgSalesClose || "0.0"}</div>
                      <div>
                        Total Appointments Kept:{" "}
                        {monthlyKpiData?.kpis?.appointmentsKept || 0}
                      </div>
                    </div>
                  }
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: "#ffffff",
                        color: "#000000",
                        fontSize: "14px",
                        padding: "14px 18px",
                        borderRadius: "10px",
                        boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15)",
                        minWidth: "220px",
                        lineHeight: "1.6",
                      },
                    },
                    arrow: { sx: { color: "#ffffff" } },
                  }}
                >
                  <div className="status-box quick-stats">
                    <div className="status-content">
                      <h3>Quick Stats</h3> <p>This month's summary</p>
                      <span>
                        <FiTrendingUp /> Avg daily points:{" "}
                        {kpis &&
                          Math.round(
                            (kpis.aggregatedPoints || 0) /
                              new Date(
                                new Date().getFullYear(),
                                new Date().getMonth() + 1,
                                0
                              ).getDate()
                          )}
                      </span>
                      <span>
                        <FiAward /> Top activity:{" "}
                        {kpis &&
                          (() => {
                            const activities = {
                              totalCalls: kpis.totalCalls || 0,
                              appointmentsKept: kpis.appointmentsKept || 0,
                              faceToFaceMeetings: kpis.faceToFaceMeetings || 0,
                              salesClosed: kpis.salesClosed || 0,
                            };
                            const maxValue = Math.max(
                              ...Object.values(activities)
                            );
                            if (maxValue === 0) return "No Activity";
                            const top = Object.keys(activities).reduce((a, b) =>
                              activities[a] > activities[b] ? a : b
                            );
                            return toTitleCase(top);
                          })()}
                      </span>
                    </div>
                    <div className="status-icon-wrapper blue">
                      <FiTrendingUp />
                    </div>
                  </div>
                </Tooltip>
              </div>
            </section>
            <section className="card quick-entry-card">
              <div
                className="quick-entry-icon"
                onClick={() => navigate("/zonal-head-dashboard/input")}
              >
                <FiPlus />
              </div>
              <h2>
                {todaysStatus.submitted ? "Edit Today's Entry" : "Quick Entry"}
              </h2>
              <button
                className="submit-entry-btn"
                onClick={() => navigate("/zonal-head-dashboard/input")}
              >
                {todaysStatus.submitted ? "Edit Entry" : "Submit Today's Entry"}
              </button>
              <span className="secure-note">
                <FiLock /> Secure & Encrypted
              </span>
            </section>

            <div className="stat-card-row">
              <section className="card stat-card">
                <div className="stat-header">
                  <h4>Aggregated Points</h4>
                  <FiAward className="stat-icon trophy" />
                </div>
                <div className="stat-main">
                  <span className="stat-value">
                    {monthlyKpiData.kpis
                      ? monthlyKpiData.kpis.aggregatedPoints || 0
                      : 0}
                  </span>
                </div>
                <p className="stat-period">
                  Last month:{" "}
                  {monthlyKpiData.previousMonthKpis
                    ? monthlyKpiData.previousMonthKpis.aggregatedPoints || 0
                    : 0}
                </p>
              </section>
              <section className="card stat-card">
                <div className="stat-header">
                  <h4>Monthly Target</h4>
                  <FiTarget className="stat-icon target" />
                </div>
                <div className="stat-main">
                  <span className="stat-value">{monthlyTargetProgress}%</span>
                </div>
                <p className="stat-period">
                  Current:{" "}
                  {monthlyKpiData.kpis
                    ? monthlyKpiData.kpis.aggregatedPoints || 0
                    : 0}{" "}
                  / {monthlyTargetPoints} points
                </p>
              </section>
              <section className="card stat-card">
                <div className="stat-header">
                  <h4>New Sales Calls</h4>
                  <FiUsers className="stat-icon interactions" />
                </div>
                <div className="stat-main">
                  <span className="stat-value">
                    {monthlyKpiData.kpis
                      ? monthlyKpiData.kpis.salesInteractions || 0
                      : 0}
                  </span>
                </div>
                <p className="stat-period">
                  Last month:{" "}
                  {monthlyKpiData.previousMonthKpis
                    ? monthlyKpiData.previousMonthKpis.salesInteractions || 0
                    : 0}
                </p>
              </section>
              <section className="card stat-card">
                <div className="stat-header">
                  <h4>Avg Daily Calls</h4>
                  <FiPhoneCall
                    className="stat-icon"
                    style={{ color: "#3498db" }}
                  />
                </div>
                <div className="stat-main">
                  <span className="stat-value">
                    {monthlyKpiData.kpis
                      ? monthlyKpiData.kpis.avgDailyCalls || "0.0"
                      : "0.0"}
                  </span>
                </div>
                <p className="stat-period">
                  Last month:{" "}
                  {monthlyKpiData.previousMonthKpis
                    ? monthlyKpiData.previousMonthKpis.avgDailyCalls || "0.0"
                    : "0.0"}
                </p>
              </section>
              <section className="card stat-card">
                <div className="stat-header">
                  <h4>Avg Sales Close</h4>
                  <FiTrendingUp
                    className="stat-icon"
                    style={{ color: "#2ecc71" }}
                  />
                </div>
                <div className="stat-main">
                  <span className="stat-value">
                    {monthlyKpiData.kpis
                      ? monthlyKpiData.kpis.avgSalesClose || "0.0"
                      : "0.0"}
                  </span>
                </div>
                <p className="stat-period">
                  Last month:{" "}
                  {monthlyKpiData.previousMonthKpis
                    ? monthlyKpiData.previousMonthKpis.avgSalesClose || "0.0"
                    : "0.0"}
                </p>
              </section>
              <section className="card stat-card">
                <div className="stat-header">
                  <h4>Appts. Kept</h4>
                  <FiCheckSquare
                    className="stat-icon"
                    style={{ color: "#9b59b6" }}
                  />
                </div>
                <div className="stat-main">
                  <span className="stat-value">
                    {monthlyKpiData.kpis
                      ? monthlyKpiData.kpis.appointmentsKept || 0
                      : 0}
                  </span>
                </div>
                <p className="stat-period">
                  Last month:{" "}
                  {monthlyKpiData.previousMonthKpis
                    ? monthlyKpiData.previousMonthKpis.appointmentsKept || 0
                    : 0}
                </p>
              </section>
              <section className="card stat-card">
                <div className="stat-header">
                  <h4>Conversion Rate</h4>
                  <FiBarChart2 className="stat-icon conversion" />
                </div>
                <div className="stat-main">
                  <span className="stat-value">
                    {monthlyKpiData.kpis
                      ? monthlyKpiData.kpis.conversionRate || 0
                      : 0}
                    %
                  </span>
                </div>
                <p className="stat-period">
                  Last month:{" "}
                  {monthlyKpiData.previousMonthKpis
                    ? monthlyKpiData.previousMonthKpis.conversionRate || 0
                    : 0}
                  %
                </p>
              </section>
            </div>

            <div className="performance-row">
              <section className="card submission-history-card">
                <div className="card-header">
                  <div className="card-title-group">
                    <h2>My Submission History</h2>
                  </div>
                  <div className="card-controls">
                    <div className="date-range-filters">
                      <span className="date-label">From</span>
                      <input
                        type="date"
                        className="date-input"
                        value={formatDateForInput(myPerformanceStartDate)}
                        onChange={(e) =>
                          setMyPerformanceStartDate(
                            new Date(e.target.value + "T00:00:00")
                          )
                        }
                      />
                      <span className="date-label">To</span>
                      <input
                        type="date"
                        className="date-input"
                        value={formatDateForInput(myPerformanceEndDate)}
                        onChange={(e) =>
                          setMyPerformanceEndDate(
                            new Date(e.target.value + "T00:00:00")
                          )
                        }
                      />
                    </div>
                  </div>
                </div>
                {isMyPerfLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 200,
                    }}
                  >
                    <CircularProgress size={30} />
                  </Box>
                ) : (
                  <div className="calendar-grid-wrapper">
                    <div className="calendar-sticky-header">
                      {["M", "T", "W", "T", "F", "S", "S"].map((day, idx) => (
                        <div key={idx} className="calendar-day-header">
                          {day}
                        </div>
                      ))}
                    </div>
                    <div className="calendar-grid">
                      {calendarDays.map((dayInfo) => {
                        if (!dayInfo.day)
                          return (
                            <div
                              key={`submission-blank-${dayInfo.key}`}
                              className="calendar-day"
                            ></div>
                          );
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const entryDateObj = new Date(
                          dayInfo.dateStr.replace(/-/g, "/")
                        );
                        const daysDiff = Math.floor(
                          (today - entryDateObj) / (1000 * 60 * 60 * 24)
                        );
                        const isClickable = entryDateObj <= today;
                        const isEditable = daysDiff >= 0 && daysDiff <= 2;
                        let className = `calendar-day ${dayInfo.status || ""}`;
                        if (isClickable) className += " clickable";
                        return (
                          <Tooltip
                            key={`submission-${dayInfo.key}`}
                            title={
                              dayInfo.status === "holiday" &&
                              dayInfo.holidayName
                                ? `${dayInfo.holidayName} (Holiday)`
                                : dayInfo.status === "submitted"
                                ? "Entry submitted"
                                : dayInfo.status === "missing"
                                ? "Entry missing"
                                : dayInfo.status === "sunday"
                                ? "Sunday"
                                : dayInfo.status === "on_leave"
                                ? "On leave"
                                : dayInfo.status === "future"
                                ? "Future date"
                                : ""
                            }
                            arrow
                            placement="top"
                          >
                            <div
                              className={className}
                              onClick={() =>
                                isClickable &&
                                navigate(
                                  `/zonal-head-dashboard/input/${dayInfo.dateStr}`
                                )
                              }
                            >
                              {dayInfo.status === "submitted" && (
                                <FiCheckCircle />
                              )}
                              {dayInfo.status === "missing" && (
                                <span className="missing-icon">&times;</span>
                              )}
                              {dayInfo.status === "holiday" && (
                                <span className="holiday-icon">🏖️</span>
                              )}
                              {dayInfo.status &&
                                !isEditable &&
                                daysDiff > 2 && (
                                  <FiLock className="lock-icon" />
                                )}
                              <span>{dayInfo.day}</span>
                            </div>
                          </Tooltip>
                        );
                      })}
                    </div>
                  </div>
                )}
                <div className="calendar-legend">
                  <div className="legend-item">
                    <span className="legend-dot submitted"></span>Submitted
                  </div>
                  <div className="legend-item">
                    <span className="legend-dot missing"></span>Missing
                  </div>
                  <div className="legend-item">
                    <span className="legend-dot on_leave"></span>On Leave
                  </div>
                  <div className="legend-item">
                    <span className="legend-dot sunday"></span>Sunday
                  </div>
                  <div className="legend-item">
                    <span className="legend-dot holiday"></span>Holiday
                  </div>
                </div>
              </section>
              <section className="card my-performance-card">
                <div className="card-header">
                  <div className="card-title-group">
                    <h2>Performance Analytics</h2>
                  </div>
                  <div className="card-controls single-row">
                    <div className="control-group">
                      <span className="control-label">From</span>
                      <input
                        type="date"
                        className="date-input"
                        value={formatDateForInput(myPerformanceStartDate)}
                        onChange={(e) =>
                          setMyPerformanceStartDate(
                            new Date(e.target.value + "T00:00:00")
                          )
                        }
                      />
                    </div>
                    <div className="control-group">
                      <span className="control-label">To</span>
                      <input
                        type="date"
                        className="date-input"
                        value={formatDateForInput(myPerformanceEndDate)}
                        onChange={(e) =>
                          setMyPerformanceEndDate(
                            new Date(e.target.value + "T00:00:00")
                          )
                        }
                      />
                    </div>
                  </div>
                </div>
                {isMyPerfLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 300,
                    }}
                  >
                    <CircularProgress size={30} />
                  </Box>
                ) : (
                  <>
                    <div
                      className="field-filters"
                      style={{
                        justifyContent: "center",
                        gap: "16px",
                        display: "flex",
                        marginBottom: "16px",
                      }}
                    >
                      <div className="field-filter">
                        <label htmlFor="field-select1">Metric 1:</label>
                        <select
                          id="field-select1"
                          value={selectedField1}
                          onChange={(e) => setSelectedField1(e.target.value)}
                          className="field-select"
                        >
                          {chartFieldOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="field-filter">
                        <label htmlFor="field-select2">Metric 2:</label>
                        <select
                          id="field-select2"
                          value={selectedField2}
                          onChange={(e) => setSelectedField2(e.target.value)}
                          className="field-select"
                        >
                          {chartFieldOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="chart-legend">
                      <div className="legend-item">
                        <span
                          className="legend-dot"
                          style={{ backgroundColor: "var(--primary-blue)" }}
                        ></span>
                        {toTitleCase(selectedField1)}
                      </div>
                      {selectedField2 !== "none" && (
                        <div className="legend-item">
                          <span
                            className="legend-dot"
                            style={{ backgroundColor: "var(--green)" }}
                          ></span>
                          {toTitleCase(selectedField2)}
                        </div>
                      )}
                    </div>
                    <div className="chart-container">
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart
                          data={performanceTrend}
                          margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            strokeOpacity={0.2}
                          />
                          <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                          <YAxis yAxisId="left" tick={{ fontSize: 10 }} />
                          {selectedField2 !== "none" && (
                            <YAxis
                              yAxisId="right"
                              orientation="right"
                              tick={{ fontSize: 10 }}
                            />
                          )}
                          <ChartTooltip
                            wrapperStyle={{ zIndex: 9999 }}
                            contentStyle={{
                              backgroundColor: "var(--card-bg, #ffffff)",
                              border: "1px solid var(--border-color, #e0e0e0)",
                              borderRadius: "8px",
                              boxShadow: "var(--card-shadow)",
                            }}
                          />
                          <Line
                            yAxisId="left"
                            type="monotone"
                            dataKey={selectedField1}
                            stroke="var(--primary-blue)"
                            strokeWidth={2}
                            dot={{ r: 4 }}
                            activeDot={{ r: 6 }}
                            name={toTitleCase(selectedField1)}
                          />
                          {selectedField2 !== "none" && (
                            <Line
                              yAxisId="right"
                              type="monotone"
                              dataKey={selectedField2}
                              stroke="var(--green)"
                              strokeWidth={2}
                              dot={{ r: 4 }}
                              activeDot={{ r: 6 }}
                              name={toTitleCase(selectedField2)}
                            />
                          )}
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </>
                )}
              </section>
            </div>

            <div className="team-performance-row">
              <section className="card rm-performance-calendar-card">
                <div className="card-header">
                  <div className="card-title-group">
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                      }}
                    >
                      {/* Breadcrumb Navigation */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          fontSize: "14px",
                          color: "#666",
                          flexWrap: "wrap",
                        }}
                      >
                        <Tooltip title="Click to return to main zonal head view">
                          <span
                            style={{
                              cursor: "pointer",
                              color: "#1976d2",
                              fontWeight: "500",
                            }}
                            onClick={() => {
                              setCalendarLevel("zonal");
                              setSelectedUserId(null);
                              setSelectedUserName("");
                              setNavigationHistory([]);
                              setSelectedTeamLeadRecordId(null);
                            }}
                          >
                            📊 Zonal Head
                          </span>
                        </Tooltip>
                        {navigationHistory.map((entry, index) => (
                          <React.Fragment
                            key={`nav-${entry.level}-${entry.userId || index}`}
                          >
                            <span style={{ color: "#999" }}>→</span>
                            <Tooltip
                              title={
                                entry.level === "branch_manager"
                                  ? `Click to view ${entry.userName}'s Team Leads`
                                  : `Click to view ${entry.userName}'s Relationship Managers`
                              }
                            >
                              <span
                                style={{
                                  cursor:
                                    index < navigationHistory.length - 1
                                      ? "pointer"
                                      : "default",
                                  color:
                                    index < navigationHistory.length - 1
                                      ? "#1976d2"
                                      : "#000",
                                  fontWeight:
                                    index === navigationHistory.length - 1
                                      ? "bold"
                                      : "normal",
                                  backgroundColor:
                                    index === navigationHistory.length - 1
                                      ? "#f0f8ff"
                                      : "transparent",
                                  padding: "2px 6px",
                                  borderRadius: "4px",
                                }}
                                onClick={() => {
                                  if (index < navigationHistory.length - 1) {
                                    // Navigate to this level
                                    const newHistory = navigationHistory.slice(
                                      0,
                                      index + 1
                                    );
                                    setNavigationHistory(newHistory);
                                    const targetEntry =
                                      newHistory[newHistory.length - 1];
                                    setCalendarLevel(targetEntry.level);
                                    setSelectedUserId(targetEntry.userId);
                                    setSelectedUserName(targetEntry.userName);
                                    if (targetEntry.level === "team_lead") {
                                      setSelectedTeamLeadRecordId(
                                        targetEntry.teamLeadRecordId || null
                                      );
                                    } else {
                                      setSelectedTeamLeadRecordId(null);
                                    }
                                  }
                                }}
                              >
                                {entry.level === "branch_manager" &&
                                  `🏢 ${entry.userName}`}
                                {entry.level === "team_lead" &&
                                  `👥 ${entry.userName}`}
                              </span>
                            </Tooltip>
                          </React.Fragment>
                        ))}
                      </div>

                      {/* Calendar Title */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          flexWrap: "wrap",
                        }}
                      >
                        {navigationHistory.length > 0 && (
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={navigateBack}
                            startIcon={<FiChevronLeft />}
                            sx={{ minWidth: "auto", px: 1 }}
                          >
                            Back
                          </Button>
                        )}
                        <h2>
                          {calendarLevel === "zonal" &&
                            (viewingDirectZonalRMs
                              ? "Zonal Head Team Calendar - Direct RMs"
                              : "Zonal Head Team Calendar")}
                          {calendarLevel === "branch_manager" &&
                            viewingBranchManagerDirectRMs
                              ? `${selectedUserName} Direct Relationship Managers Performance`
                              : `${selectedUserName} Team Leads Calendar`}
                          {calendarLevel === "team_lead" &&
                            `${selectedUserName} RMs Calendar`}
                        </h2>
                        {calendarLevel === "zonal" &&
                          navigationHistory.length === 0 && (
                            <Tooltip title="💡 Tip: Click 'Explore Teams' to drill down into team performance">
                              <Box
                                sx={{
                                  backgroundColor: "#e3f2fd",
                                  color: "#1976d2",
                                  padding: "4px 8px",
                                  borderRadius: "4px",
                                  fontSize: "12px",
                                  fontWeight: "500",
                                  cursor: "help",
                                }}
                              >
                                How to navigate →
                              </Box>
                            </Tooltip>
                          )}
                      </div>
                    </div>
                    {calendarLevel === "zonal" && (
                      <Tooltip
                        title={(() => {
                          if (hierarchyInfo?.hasBranchManagers)
                            return "Click to see all Branch Managers and drill down into their teams";
                          if (hierarchyInfo?.hasDirectTeamLeads)
                            return "Click to see all Team Leads and drill down into their Relationship Managers";
                          if (hierarchyInfo?.hasDirectRelationshipManagers)
                            return "Click to see all Relationship Managers directly under this Zonal Head";
                          return "Click to explore all team members under this Zonal Head";
                        })()}
                      >
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => setShowBranchManagersModal(true)}
                          startIcon={<FiUsers />}
                        >
                          {(() => {
                            return "Explore Teams";
                          })()}
                        </Button>
                      </Tooltip>
                    )}
                    {calendarLevel === "zonal" && viewingDirectZonalRMs && (
                      <Button
                        variant="text"
                        size="small"
                        onClick={resetDirectZonalRMView}
                      >
                        View Branch Managers
                      </Button>
                    )}
                    {calendarLevel === "branch_manager" && !viewingBranchManagerDirectRMs && (
                      <Tooltip title="Click to see Team Leads under this Branch Manager and drill down further">
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => setShowCurrentLevelUsersModal(true)}
                          startIcon={<FiUsers />}
                        >
                          Explore Team Leads
                        </Button>
                      </Tooltip>
                    )}
                    {calendarLevel === "team_lead" && (
                      <Tooltip title="Click to see Relationship Managers under this Team Lead">
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => setShowCurrentLevelUsersModal(true)}
                          startIcon={<FiUsers />}
                        >
                          View Relationship Managers
                        </Button>
                      </Tooltip>
                    )}
                  </div>
                  <div className="card-controls">
                    <div className="date-range-filters">
                      <span className="date-label">From</span>
                      <input
                        type="date"
                        className="date-input"
                        value={formatDateForInput(branchCalendarStartDate)}
                        onChange={(e) =>
                          setBranchCalendarStartDate(
                            new Date(e.target.value + "T00:00:00")
                          )
                        }
                      />
                      <span className="date-label">To</span>
                      <input
                        type="date"
                        className="date-input"
                        value={formatDateForInput(branchCalendarEndDate)}
                        onChange={(e) =>
                          setBranchCalendarEndDate(
                            new Date(e.target.value + "T00:00:00")
                          )
                        }
                      />
                    </div>
                  </div>
                </div>
                {isBranchCalendarLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 200,
                    }}
                  >
                    <CircularProgress size={30} />
                  </Box>
                ) : (
                  <div className="calendar-grid-wrapper">
                    <div className="calendar-sticky-header">
                      {["M", "T", "W", "T", "F", "S", "S"].map((day, idx) => (
                        <div key={idx} className="calendar-day-header">
                          {day}
                        </div>
                      ))}
                    </div>
                    <div className="calendar-grid">
                      {branchPerformanceCalendarDays.map((dayInfo) => (
                        <Tooltip
                          key={dayInfo.key}
                          title={
                            dayInfo.status === "holiday" && dayInfo.holidayName
                              ? dayInfo.holidayName
                              : ""
                          }
                          arrow
                          placement="top"
                        >
                          <div
                            className={`calendar-day ${dayInfo.status || ""} ${
                              dayInfo.day && dayInfo.status !== "future"
                                ? "clickable"
                                : ""
                            }`}
                            onClick={() =>
                              dayInfo.day &&
                              dayInfo.status &&
                              dayInfo.status !== "future" &&
                              handleCalendarDateClick(dayInfo.dateStr)
                            }
                          >
                            {dayInfo.day ? (
                              <>
                                {" "}
                                {dayInfo.status === "submitted" && (
                                  <FiCheckCircle />
                                )}{" "}
                                {dayInfo.status === "holiday" && (
                                  <span className="holiday-indicator">🏖️</span>
                                )}{" "}
                                {dayInfo.status !== "future" && (
                                  <span className="points">
                                    {dayInfo.individualStatus
                                      ? // Show individual status for single person views
                                        dayInfo.individualStatus === "submitted"
                                        ? "✓"
                                        : dayInfo.individualStatus === "missing"
                                        ? "✗"
                                        : dayInfo.individualStatus ===
                                          "both_submitted"
                                        ? "✓✓"
                                        : dayInfo.individualStatus ===
                                          "one_submitted"
                                        ? "✓✗"
                                        : dayInfo.individualStatus ===
                                          "none_submitted"
                                        ? "✗✗"
                                        : dayInfo.percentage + "%"
                                      : // Show percentage for group views
                                        dayInfo.percentage + "%"}
                                  </span>
                                )}{" "}
                                <span>{dayInfo.day}</span>{" "}
                              </>
                            ) : (
                              <>&nbsp;</>
                            )}
                          </div>
                        </Tooltip>
                      ))}
                    </div>
                  </div>
                )}
                <div className="calendar-legend">
                  <div className="legend-item">
                    <span className="legend-dot submitted"></span>Submitted
                  </div>
                  <div className="legend-item">
                    <span className="legend-dot missing"></span>Missing
                  </div>
                  <div className="legend-item">
                    <span className="legend-dot on_leave"></span>On Leave
                  </div>
                  <div className="legend-item">
                    <span className="legend-dot sunday"></span>Sunday
                  </div>
                  <div className="legend-item">
                    <span className="legend-dot holiday"></span>Holiday
                  </div>
                </div>
              </section>

              <section className="card team-performance-card">
                <div className="card-header">
                  <div className="card-title-group">
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                      }}
                    >
                      {/* Breadcrumb Navigation */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          fontSize: "14px",
                          color: "#666",
                          flexWrap: "wrap",
                        }}
                      >
                        <Tooltip title="Click to return to main zonal head view">
                          <span
                            style={{
                              cursor: "pointer",
                              color: "#1976d2",
                              fontWeight: "500",
                            }}
                            onClick={() => {
                              setChartLevel("zonal");
                              setChartSelectedUserId(null);
                              setChartSelectedUserName("");
                              setChartNavigationHistory([]);
                              setChartViewingDirectZonalRMs(false);
                              setChartDirectZonalRMs([]);
                              setChartViewingBranchManagerDirectRMs(false);
                              setChartBranchManagerDirectRMs([]);
                            }}
                          >
                            📊 Zonal Head
                          </span>
                        </Tooltip>
                        {chartNavigationHistory.map((entry, index) => (
                          <React.Fragment
                            key={`chart-nav-${entry.level}-${
                              entry.userId || index
                            }`}
                          >
                            <span style={{ color: "#999" }}>→</span>
                            <Tooltip
                              title={
                                entry.level === "branch_manager"
                                  ? `Click to view ${entry.userName}'s Team Leads`
                                  : `Click to view ${entry.userName}'s Relationship Managers`
                              }
                            >
                              <span
                                style={{
                                  cursor:
                                    index < chartNavigationHistory.length - 1
                                      ? "pointer"
                                      : "default",
                                  color:
                                    index < chartNavigationHistory.length - 1
                                      ? "#1976d2"
                                      : "#000",
                                  fontWeight:
                                    index === chartNavigationHistory.length - 1
                                      ? "bold"
                                      : "normal",
                                  backgroundColor:
                                    index === chartNavigationHistory.length - 1
                                      ? "#f0f8ff"
                                      : "transparent",
                                  padding: "2px 6px",
                                  borderRadius: "4px",
                                }}
                                onClick={() => {
                                  if (
                                    index <
                                    chartNavigationHistory.length - 1
                                  ) {
                                    // Navigate to this level
                                    const newHistory =
                                      chartNavigationHistory.slice(
                                        0,
                                        index + 1
                                      );
                                    setChartNavigationHistory(newHistory);
                                    const targetEntry =
                                      newHistory[newHistory.length - 1];
                                    setChartLevel(targetEntry.level);
                                    setChartSelectedUserId(targetEntry.userId);
                                    setChartSelectedUserName(
                                      targetEntry.userName
                                    );
                                    if (targetEntry.level === "zonal") {
                                      setChartViewingDirectZonalRMs(false);
                                      setChartDirectZonalRMs([]);
                                    }
                                    setChartViewingBranchManagerDirectRMs(
                                      false
                                    );
                                    setChartBranchManagerDirectRMs([]);
                                  }
                                }}
                              >
                                {entry.level === "branch_manager" &&
                                  `🏢 ${entry.userName}`}
                                {entry.level === "team_lead" &&
                                  `👥 ${entry.userName}`}
                              </span>
                            </Tooltip>
                          </React.Fragment>
                        ))}
                      </div>

                      {/* Chart Title */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          flexWrap: "wrap",
                        }}
                      >
                        {chartNavigationHistory.length > 0 && (
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={navigateChartBack}
                            startIcon={<FiChevronLeft />}
                            sx={{ minWidth: "auto", px: 1 }}
                          >
                            Back
                          </Button>
                        )}
                        <h2>
                          {chartLevel === "zonal" &&
                            (chartViewingDirectZonalRMs
                              ? "Branch Manager Performance - Direct RMs"
                              : hierarchyInfo.hasBranchManagers
                              ? "Branch Manager Performance"
                              : hierarchyInfo.hasDirectTeamLeads
                              ? "Team Lead Performance"
                              : hierarchyInfo.hasDirectRelationshipManagers
                              ? "Relationship Manager Performance"
                              : "Team Performance")}
                          {chartLevel === "branch_manager" &&
                            (chartViewingBranchManagerDirectRMs
                              ? `${chartSelectedUserName} Direct Relationship Managers Performance`
                              : `${chartSelectedUserName} Team Leads Performance`)}
                          {chartLevel === "team_lead" &&
                            `${chartSelectedUserName} Relationship Managers Performance`}
                        </h2>
                        {chartLevel === "zonal" &&
                          chartViewingDirectZonalRMs && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: "inline-block" }}
                            >
                              Charts currently showing direct Relationship
                              Managers.
                            </Typography>
                          )}
                        {chartLevel === "branch_manager" &&
                          chartViewingBranchManagerDirectRMs && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: "inline-block" }}
                            >
                              Charts currently showing direct Relationship
                              Managers under {chartSelectedUserName}.
                            </Typography>
                          )}
                        {chartLevel === "zonal" &&
                          chartNavigationHistory.length === 0 && (
                            <Tooltip title="💡 Tip: Click 'Explore Teams' to drill down into team performance">
                              <Box
                                sx={{
                                  backgroundColor: "#e3f2fd",
                                  color: "#1976d2",
                                  padding: "4px 8px",
                                  borderRadius: "4px",
                                  fontSize: "12px",
                                  fontWeight: "500",
                                  cursor: "help",
                                }}
                              >
                                How to navigate →
                              </Box>
                            </Tooltip>
                          )}
                      </div>
                    </div>

                    {chartLevel === "zonal" && (
                      <Tooltip
                        title={(() => {
                          if (hierarchyInfo?.hasBranchManagers)
                            return "Click to see all Branch Managers and drill down into their teams";
                          if (hierarchyInfo?.hasDirectTeamLeads)
                            return "Click to see all Team Leads and drill down into their Relationship Managers";
                          if (hierarchyInfo?.hasDirectRelationshipManagers)
                            return "Click to see all Relationship Managers directly under this Zonal Head";
                          return "Click to explore all team members under this Zonal Head";
                        })()}
                      >
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => setShowChartBranchManagersModal(true)}
                          startIcon={<FiUsers />}
                        >
                          {(() => {
                            return "Explore Teams";
                          })()}
                        </Button>
                      </Tooltip>
                    )}
                    {chartLevel === "zonal" && chartViewingDirectZonalRMs && (
                      <Button
                        variant="text"
                        size="small"
                        onClick={resetChartDirectRMView}
                      >
                        View Branch Managers
                      </Button>
                    )}
                    {chartLevel === "branch_manager" && !chartViewingBranchManagerDirectRMs && (
                      <Tooltip title="Click to see Team Leads under this Branch Manager and drill down further">
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() =>
                            setShowChartCurrentLevelUsersModal(true)
                          }
                          startIcon={<FiUsers />}
                        >
                          Explore Team Leads
                        </Button>
                      </Tooltip>
                    )}
                    {chartLevel === "team_lead" && (
                      <Tooltip title="Click to see Relationship Managers under this Team Lead">
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() =>
                            setShowChartCurrentLevelUsersModal(true)
                          }
                          startIcon={<FiUsers />}
                        >
                          View Relationship Managers
                        </Button>
                      </Tooltip>
                    )}
                  </div>
                  <div className="card-controls single-row">
                    <div className="control-group">
                      <span className="control-label">Metric:</span>
                      <select
                        id="team-field-select"
                        value={selectedPerfMetric}
                        onChange={(e) => setSelectedPerfMetric(e.target.value)}
                        className="field-select"
                      >
                        {performanceChartMetrics.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="control-group">
                      <span className="control-label">From</span>
                      <input
                        type="date"
                        className="date-input"
                        value={formatDateForInput(branchPerformanceStartDate)}
                        onChange={(e) =>
                          setBranchPerformanceStartDate(
                            new Date(e.target.value + "T00:00:00")
                          )
                        }
                      />
                    </div>
                    <div className="control-group">
                      <span className="control-label">To</span>
                      <input
                        type="date"
                        className="date-input"
                        value={formatDateForInput(branchPerformanceEndDate)}
                        onChange={(e) =>
                          setBranchPerformanceEndDate(
                            new Date(e.target.value + "T00:00:00")
                          )
                        }
                      />
                    </div>
                  </div>
                </div>
                {isBranchPerfLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 300,
                    }}
                  >
                    <CircularProgress size={30} />
                  </Box>
                ) : chartViewMode === "chart" ? (
                  chartData && chartData.length > 0 ? (
                    <div className="chart-container">
                      <ResponsiveContainer width="100%" height={360}>
                        <BarChart
                          data={chartData}
                          margin={{ top: 5, right: 20, left: 10, bottom: 50 }}
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            strokeOpacity={0.2}
                          />
                          <XAxis
                            dataKey="displayName"
                            interval={0}
                            tick={{
                              fontSize: 12,
                              angle: -35,
                              textAnchor: "end",
                            }}
                          />
                          <YAxis />
                          <ChartTooltip
                            cursor={{ fill: "var(--faint-bg, #f4f7fa)" }}
                            wrapperStyle={{ zIndex: 9999 }}
                            contentStyle={{
                              backgroundColor: "var(--card-bg, #ffffff)",
                              border: "1px solid var(--border-color, #e0e0e0)",
                              borderRadius: "8px",
                              boxShadow: "var(--card-shadow)",
                            }}
                            labelFormatter={(label, payload) =>
                              payload && payload.length > 0
                                ? payload[0]?.payload?.displayName || label
                                : label
                            }
                          />
                          <Bar
                            dataKey={selectedPerfMetric}
                            name={
                              performanceChartMetrics.find(
                                (opt) => opt.value === selectedPerfMetric
                              )?.label || selectedPerfMetric
                            }
                            fill="#4A90E2"
                            radius={[4, 4, 0, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div
                      className="chart-container"
                      style={{
                        height: "300px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                        padding: "20px",
                        textAlign: "center",
                      }}
                    >
                      <Typography color="textSecondary" gutterBottom>
                        No Branch Manager performance data available for the
                        selected range.
                      </Typography>
                    </div>
                  )
                ) : (
                  <div className="calendar-grid-wrapper">
                    <div className="calendar-sticky-header">
                      {["M", "T", "W", "T", "F", "S", "S"].map((day, idx) => (
                        <div key={idx} className="calendar-day-header">
                          {day}
                        </div>
                      ))}
                    </div>
                    <div className="calendar-grid">
                      {branchPerformanceCalendarDays.map((dayInfo) => (
                        <Tooltip
                          key={dayInfo.key}
                          title={
                            dayInfo.status === "holiday" && dayInfo.holidayName
                              ? dayInfo.holidayName
                              : ""
                          }
                          arrow
                          placement="top"
                        >
                          <div
                            className={`calendar-day ${dayInfo.status || ""} ${
                              dayInfo.day && dayInfo.status !== "future"
                                ? "clickable"
                                : ""
                            }`}
                            onClick={() =>
                              dayInfo.day &&
                              dayInfo.status &&
                              dayInfo.status !== "future" &&
                              handleCalendarDateClick(dayInfo.dateStr)
                            }
                          >
                            {dayInfo.day ? (
                              <>
                                {" "}
                                {dayInfo.status === "submitted" && (
                                  <FiCheckCircle />
                                )}{" "}
                                {dayInfo.status === "holiday" && (
                                  <span className="holiday-indicator">🏖️</span>
                                )}{" "}
                                {dayInfo.status !== "future" && (
                                  <span className="points">
                                    {dayInfo.individualStatus
                                      ? // Show individual status for single person views
                                        dayInfo.individualStatus === "submitted"
                                        ? "✓"
                                        : dayInfo.individualStatus === "missing"
                                        ? "✗"
                                        : dayInfo.individualStatus ===
                                          "both_submitted"
                                        ? "✓✓"
                                        : dayInfo.individualStatus ===
                                          "one_submitted"
                                        ? "✓✗"
                                        : dayInfo.individualStatus ===
                                          "none_submitted"
                                        ? "✗✗"
                                        : dayInfo.percentage + "%"
                                      : // Show percentage for group views
                                        dayInfo.percentage + "%"}
                                  </span>
                                )}{" "}
                                <span>{dayInfo.day}</span>{" "}
                              </>
                            ) : (
                              <>&nbsp;</>
                            )}
                          </div>
                        </Tooltip>
                      ))}
                    </div>
                  </div>
                )}
              </section>
            </div>

            <section className="card leaderboard-card">
              <div className="card-header">
                <h2>
                  {hierarchyInfo.hasBranchManagers
                    ? "Branch Manager"
                    : hierarchyInfo.hasDirectTeamLeads
                    ? "Team Lead"
                    : "Relationship Manager"}{" "}
                  Rankings
                </h2>
                <div className="card-controls">
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <Select
                      value={selectedLeaderboardMetric}
                      onChange={(e) =>
                        setSelectedLeaderboardMetric(e.target.value)
                      }
                      displayEmpty
                    >
                      {leaderboardFieldOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              </div>
              {isBranchDetailsLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: 150,
                  }}
                >
                  <CircularProgress size={30} />
                </Box>
              ) : (
                <div
                  className="leaderboard-list"
                  style={
                    getTopPerformers.length > 3
                      ? { maxHeight: "300px", overflowY: "auto" }
                      : {}
                  }
                >
                  {getTopPerformers.length > 0 ? (
                    getTopPerformers.map((member, index) => (
                      <div
                        key={`leaderboard-${
                          member.userId || member.id || index
                        }`}
                        className="leaderboard-item"
                      >
                        <span className="rank">{index + 1}</span>
                        <span className="name">
                          {member.name || member.username}
                        </span>
                        <span className="points">
                          {getMetric(member, selectedLeaderboardMetric) || 0}{" "}
                          {leaderboardFieldOptions
                            .find(
                              (opt) => opt.value === selectedLeaderboardMetric
                            )
                            ?.label.includes("Points")
                            ? "pts"
                            : ""}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div
                      style={{
                        padding: "20px",
                        textAlign: "center",
                        color: "#666",
                      }}
                    >
                      <Typography>
                        No team leads found or no performance data available
                      </Typography>
                    </div>
                  )}
                </div>
              )}
              {getTopPerformers.length > 0 &&
                getTopPerformers.every(
                  (rm) => getMetric(rm, selectedLeaderboardMetric) === 0
                ) && (
                  <div
                    style={{
                      padding: "8px 16px",
                      fontSize: "12px",
                      color: "#666",
                      borderTop: "1px solid #f0f0f0",
                    }}
                  >
                    <strong>Note:</strong> Team Leads are ranked, but all have
                    zero values for the selected metric.
                  </div>
                )}
            </section>
          </div>
        )}

        {currentSection === "monthly-report" && <MonthlyReport />}
        {currentSection === "user-management" && <ZonalHeadUserManagement />}
        {currentSection === "settings" && (
          <ProfilePage
            profile={user}
            profilePhotoUrl={user.profile_photo}
            onProfileUpdate={fetchInitialStaticData}
          />
        )}
        {currentSection === "notifications" && (
          <div className="notifications-section">
            <h2>Notifications & Approvals</h2>
            <div className="notifications-tabs">
              <button
                className={`tab-button ${
                  selectedNotificationTab === "pending-approvals"
                    ? "active"
                    : ""
                }`}
                onClick={() => setSelectedNotificationTab("pending-approvals")}
              >
                Pending Approvals ({filteredPendingLeaveRequests.length})
              </button>
              <button
                className={`tab-button ${
                  selectedNotificationTab === "approved-requests"
                    ? "active"
                    : ""
                }`}
                onClick={() => setSelectedNotificationTab("approved-requests")}
              >
                Staff on Leave ({approvedLeaveRequests.length})
              </button>
              <button
                className={`tab-button ${
                  selectedNotificationTab === "rm-submissions" ? "active" : ""
                }`}
                onClick={() => setSelectedNotificationTab("rm-submissions")}
              >
                Branch Submissions
              </button>
              <button
                className={`tab-button ${
                  selectedNotificationTab === "my-requests" ? "active" : ""
                }`}
                onClick={() => setSelectedNotificationTab("my-requests")}
              >
                My Leave Requests
              </button>
              <button
                className={`tab-button ${
                  selectedNotificationTab === "all-notifications"
                    ? "active"
                    : ""
                }`}
                onClick={() => setSelectedNotificationTab("all-notifications")}
              >
                All Notifications
              </button>
            </div>
            {selectedNotificationTab === "my-requests" && (
              <div className="pending-requests-list">
                <h3>My Leave Requests</h3>
                {ownLeaveRequests.length > 0 ? (
                  ownLeaveRequests.map((request) => (
                    <div
                      key={`own-leave-${request.leaveRequestId}`}
                      className="leave-request-item"
                    >
                      <p>
                        <strong>Your</strong> leave request from{" "}
                        <strong>
                          {formatNotificationDate(request.startDate)}
                        </strong>
                        {request.endDate ? (
                          <>
                            {" to "}
                            <strong>
                              {formatNotificationDate(request.endDate)}
                            </strong>
                          </>
                        ) : (
                          ""
                        )}
                        .
                      </p>
                      <p>
                        <strong>Reason:</strong> {request.reason}
                      </p>
                      <div className="status-badge">
                        <span className={`status ${request.status}`}>
                          {toTitleCase(request.status)}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="empty-state">
                    <Typography color="textSecondary">
                      No personal leave requests found.
                    </Typography>
                  </div>
                )}
              </div>
            )}
            {selectedNotificationTab === "rm-submissions" && (
              <div className="rm-submissions-list">
                <h3>Recent Branch Entry Submissions (Last 7 Days)</h3>
                {branchSubmissions.length > 0 ? (
                  branchSubmissions.map((submission, index) => (
                    <div
                      key={`submission-item-${index}`}
                      className="submission-item"
                    >
                      <span className="submission-indicator submitted" />
                      <div className="submission-info">
                        <Typography variant="body1">
                          <strong>{submission.rmName}</strong> submitted entry
                          for{" "}
                          <strong>
                            {formatNotificationDate(submission.date)}
                          </strong>
                          .
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {submission.points} points •{" "}
                          {submission.telephonesalescalls} calls •{" "}
                          {submission.salesclose} sales
                        </Typography>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="empty-state">
                    <Typography color="textSecondary">
                      No recent submissions from your branch members.
                    </Typography>
                  </div>
                )}
              </div>
            )}
            {selectedNotificationTab === "pending-approvals" && (
              <div className="pending-approvals-list">
                <h3>Leave Requests Pending Approval</h3>
                {filteredPendingLeaveRequests.length > 0 ? (
                  filteredPendingLeaveRequests.map((request) => {
                    const tlNameForRM = request.reportsToName || "N/A";
                    return (
                      <div
                        key={`pending-${request.leaveRequestId}`}
                        className="leave-request-item"
                      >
                        <div className="request-header">
                          <Typography variant="body1">
                            {request.requestorRole === "branch_manager" ? (
                              <>
                                <span className="role-badge bm-badge">
                                  Branch Manager
                                </span>{" "}
                                <strong>{request.requestorName}</strong>
                              </>
                            ) : request.requestorRole === "team_lead" ? (
                              <>
                                <span className="role-badge tl-badge">
                                  Team Lead
                                </span>{" "}
                                <strong>{request.requestorName}</strong>
                                {tlNameForRM && tlNameForRM !== "N/A" && (
                                  <> (reports to {tlNameForRM})</>
                                )}
                              </>
                            ) : (
                              <>
                                <span className="role-badge rm-badge">RM</span>{" "}
                                <strong>{request.requestorName}</strong>{" "}
                                (reports to {tlNameForRM})
                              </>
                            )}{" "}
                            has requested leave from{" "}
                            <strong>
                              {formatNotificationDate(request.startDate)}
                            </strong>
                            {request.endDate && (
                              <>
                                {" to "}
                                <strong>
                                  {formatNotificationDate(request.endDate)}
                                </strong>
                              </>
                            )}
                            .
                          </Typography>
                          <div className="request-actions">
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() =>
                                handleApproveLeaveRequest(
                                  request.leaveRequestId
                                )
                              }
                            >
                              Approve
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              onClick={() =>
                                handleOpenLeaveRejectDialog(
                                  request.leaveRequestId
                                )
                              }
                            >
                              Reject
                            </Button>
                          </div>
                        </div>
                        <Typography variant="body2">
                          <strong>Reason:</strong> {request.reason}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Requested on:{" "}
                          {formatNotificationDate(request.requestedAt)}
                        </Typography>
                      </div>
                    );
                  })
                ) : (
                  <div className="empty-state">
                    <FiCheckCircle size={40} style={{ color: "#ccc" }} />
                    <Typography color="textSecondary" sx={{ mt: 2 }}>
                      No pending leave requests from your subordinates.
                    </Typography>
                  </div>
                )}
              </div>
            )}
            {selectedNotificationTab === "approved-requests" && (
              <div className="approved-requests-list">
                <h3>Staff on Leave</h3>
                {/* Show team lead leave approval notifications */}
                {notifications
                  .filter(
                    (notification) =>
                      notification.type === "team_lead_leave_approved"
                  )
                  .map((notification) => (
                    <div
                      key={`notification-${notification.id}`}
                      className="leave-request-item"
                    >
                      <div className="request-header">
                        <Typography variant="body1">
                          <span className="role-badge tl-badge">
                            Team Lead Leave
                          </span>{" "}
                          <strong>{notification.message}</strong>
                        </Typography>
                      </div>
                      <Typography variant="caption" color="textSecondary">
                        {formatNotificationDate(notification.date)}
                      </Typography>
                    </div>
                  ))}
                {/* Show approved leave requests */}
                {approvedLeaveRequests.length > 0 ? (
                  approvedLeaveRequests.map((request) => {
                    const tlNameForRM = request.reportsToName || "N/A";
                    return (
                      <div
                        key={`approved-${request.leaveRequestId}`}
                        className="leave-request-item"
                      >
                        <div className="request-header">
                          <Typography variant="body1">
                            {request.requestorRole === "branch_manager" ? (
                              <>
                                <span className="role-badge bm-badge">
                                  Branch Manager
                                </span>{" "}
                                <strong>{request.requestorName}</strong>
                              </>
                            ) : request.requestorRole === "team_lead" ? (
                              <>
                                <span className="role-badge tl-badge">
                                  Team Lead
                                </span>{" "}
                                <strong>{request.requestorName}</strong>
                                {tlNameForRM && tlNameForRM !== "N/A" && (
                                  <> (reports to {tlNameForRM})</>
                                )}
                              </>
                            ) : (
                              <>
                                <span className="role-badge rm-badge">RM</span>{" "}
                                <strong>{request.requestorName}</strong>{" "}
                                (reports to {tlNameForRM})
                              </>
                            )}{" "}
                            is on leave from{" "}
                            <strong>
                              {formatNotificationDate(request.startDate)}
                            </strong>
                            {request.endDate && (
                              <>
                                {" to "}
                                <strong>
                                  {formatNotificationDate(request.endDate)}
                                </strong>
                              </>
                            )}
                            .
                          </Typography>
                          <div className="request-actions">
                            <Button
                              size="small"
                              variant="outlined"
                              color="warning"
                              onClick={() =>
                                handleOpenLeaveRevokeDialog(
                                  request.leaveRequestId
                                )
                              }
                            >
                              Revoke
                            </Button>
                          </div>
                        </div>
                        <Typography variant="body2">
                          <strong>Reason:</strong> {request.reason}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Approved on:{" "}
                          {formatNotificationDate(request.approvedAt)}
                        </Typography>
                      </div>
                    );
                  })
                ) : notifications.filter(
                    (notification) =>
                      notification.type === "team_lead_leave_approved"
                  ).length === 0 ? (
                  <div className="empty-state">
                    <Typography color="textSecondary">
                      No staff currently on leave.
                    </Typography>
                  </div>
                ) : null}
              </div>
            )}
            {selectedNotificationTab === "all-notifications" && (
              <div className="notifications-list">
                <h3>All Notifications</h3>
                {notifications.length > 0 ? (
                  notifications.map((notification) => (
                    <div key={notification.id} className="notification-item">
                      <div className="notification-content">
                        <Typography variant="body1">
                          {notification.message}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {formatNotificationDate(notification.date)}
                        </Typography>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="empty-state">
                    <Typography color="textSecondary">
                      No notifications available.
                    </Typography>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </main>

      <Dialog
        open={showMissingEntryModal}
        onClose={() => setShowMissingEntryModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{modalTitle}</DialogTitle>
        <DialogContent>
          <Typography variant="h6" gutterBottom>
            ✅ Submitted ({modalSubmittedList.length}):
          </Typography>
          {modalSubmittedList.length > 0 ? (
            <TableContainer component={Paper} sx={{ mt: 1, mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell align="right">Points</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {modalSubmittedList.map((item) => (
                    <TableRow key={`submitted-item-${item.userId}`}>
                      <TableCell>
                        {item.displayName || formatMemberNameWithRole(item)}
                      </TableCell>
                      <TableCell align="right">{item.points || 0}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ mt: 1, mb: 3 }}
            >
              None.
            </Typography>
          )}
          <Typography variant="h6" gutterBottom>
            🟨 On Leave ({modalOnLeaveList.length}):
          </Typography>
          {modalOnLeaveList.length > 0 ? (
            <TableContainer component={Paper} sx={{ mt: 1, mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {modalOnLeaveList.map((item) => (
                    <TableRow key={`onleave-item-${item.userId}`}>
                      <TableCell>
                        {item.displayName || formatMemberNameWithRole(item)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ mt: 1, mb: 3 }}
            >
              None.
            </Typography>
          )}
          <Typography variant="h6" gutterBottom>
            ❌ Missing ({modalMissingList.length}):
          </Typography>
          {modalMissingList.length > 0 ? (
            <TableContainer component={Paper} sx={{ mt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {modalMissingList.map((item, index) => (
                    <TableRow
                      key={`missing-item-${item.userId || item.id || index}`}
                    >
                      <TableCell>
                        {item.displayName || formatMemberNameWithRole(item)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              All other active members have submitted.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowMissingEntryModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={leaveRequestDialogOpen}
        onClose={() => setLeaveRequestDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Request Leave</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Submit a leave request.
          </Typography>
          <TextField
            label="Reason"
            value={leaveRequestForm.reason}
            onChange={(e) =>
              setLeaveRequestForm({
                ...leaveRequestForm,
                reason: e.target.value,
              })
            }
            fullWidth
            required
            margin="normal"
            multiline
            rows={3}
            variant="outlined"
          />
          <TextField
            label="Start Date"
            type="date"
            value={leaveRequestForm.start_date}
            onChange={(e) =>
              setLeaveRequestForm({
                ...leaveRequestForm,
                start_date: e.target.value,
              })
            }
            fullWidth
            required
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="End Date"
            type="date"
            value={leaveRequestForm.end_date}
            onChange={(e) =>
              setLeaveRequestForm({
                ...leaveRequestForm,
                end_date: e.target.value,
              })
            }
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLeaveRequestDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitLeaveRequest}
            variant="contained"
            color="primary"
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={leaveRejectDialogOpen}
        onClose={() => setLeaveRejectDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject Leave Request</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Please provide a reason for rejecting the leave request.
          </Typography>
          <TextField
            label="Rejection Reason"
            value={leaveRejectionReason}
            onChange={(e) => setLeaveRejectionReason(e.target.value)}
            fullWidth
            margin="normal"
            multiline
            rows={4}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLeaveRejectDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleRejectLeaveRequest}
            variant="contained"
            color="error"
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={leaveRevokeDialogOpen}
        onClose={() => setLeaveRevokeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Revoke Leave Approval</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Please provide a reason for revoking the approved leave request.
          </Typography>
          <TextField
            label="Revocation Reason"
            value={leaveRevocationReason}
            onChange={(e) => setLeaveRevocationReason(e.target.value)}
            fullWidth
            margin="normal"
            multiline
            rows={4}
            variant="outlined"
            placeholder="e.g., Urgent project requirements, Business needs changed, etc."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLeaveRevokeDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleRevokeLeaveRequest}
            variant="contained"
            color="error"
          >
            Revoke Approval
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={noTeamLeadsDialogOpen}
        onClose={() => setNoTeamLeadsDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>No Team Leads Found</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1">
            {noTeamLeadsBranchName
              ? `${noTeamLeadsBranchName} does not have any team leads assigned.`
              : "No team leads are available under this branch manager."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoTeamLeadsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={noDirectRMsDialogOpen}
        onClose={() => setNoDirectRMsDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>No Relationship Managers Found</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1">
            {noDirectRMsBranchName
              ? `${noDirectRMsBranchName} does not have any relationship managers assigned.`
              : "No relationship managers are available under this branch manager."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoDirectRMsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showBranchManagersModal}
        onClose={() => setShowBranchManagersModal(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Button
              variant="text"
              size="small"
              onClick={() => setShowBranchManagersModal(false)}
              startIcon={<FiChevronLeft />}
              sx={{ minWidth: "auto", px: 1 }}
            >
              Back
            </Button>
            <Typography variant="h6">Teams under {userName}</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {branchMembers.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="body1">No teams found</Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {branchMembers.some((m) => m.role === "branch_manager") && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
                    Branch Manager Teams
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(300px, 1fr))",
                      gap: 2,
                    }}
                  >
                    {branchMembers
                      .filter((m) => m.role === "branch_manager")
                      .map((leader) => (
                        <Card key={leader.id} sx={{ border: "1px solid #ddd" }}>
                          <CardContent sx={{ p: 2 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                mb: 1,
                              }}
                            >
                              <Box>
                                <Typography
                                  variant="h6"
                                  sx={{ fontSize: "1rem" }}
                                >
                                  {leader.name}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Branch Manager
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 1,
                                }}
                              >
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={() => handleMemberSelect(leader)}
                                >
                                  View Team
                                </Button>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() =>
                                    handleBranchManagerDirectRMsSelect(leader)
                                  }
                                >
                                  View Direct RM
                                </Button>
                              </Box>
                            </Box>
                            {leader.members && leader.members.length > 0 && (
                              <Box sx={{ mt: 1 }}>
                                <Typography
                                  variant="body2"
                                  sx={{ mb: 1, fontSize: "0.8rem" }}
                                >
                                  Team Members ({leader.members.length}):
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 0.5,
                                  }}
                                >
                                  {(() => {
                                    // Remove duplicates from team members
                                    const uniqueMembers = leader.members.filter(
                                      (member, index, self) =>
                                        index ===
                                        self.findIndex(
                                          (m) =>
                                            m.userId === member.userId ||
                                            m.id === member.id
                                        )
                                    );
                                    return (
                                      <>
                                        {uniqueMembers.slice(0, 4).map((m) => (
                                          <Chip
                                            key={`${m.id || m.userId}-${
                                              m.name
                                            }`}
                                            label={`${m.name} (${m.role.replace(
                                              "_",
                                              " "
                                            )})`}
                                            size="small"
                                            variant="outlined"
                                            sx={{
                                              fontSize: "0.7rem",
                                              height: "20px",
                                            }}
                                          />
                                        ))}
                                        {uniqueMembers.length > 4 && (
                                          <Chip
                                            label={`+${
                                              uniqueMembers.length - 4
                                            } more`}
                                            size="small"
                                            sx={{
                                              fontSize: "0.7rem",
                                              height: "20px",
                                            }}
                                          />
                                        )}
                                      </>
                                    );
                                  })()}
                                </Box>
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                  </Box>
                </Box>
              )}

              {branchMembers.some((m) => m.role === "team_lead") && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
                    Direct Team Lead Teams
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(300px, 1fr))",
                      gap: 2,
                    }}
                  >
                    {branchMembers
                      .filter((m) => m.role === "team_lead")
                      .map((leader) => (
                        <Card key={leader.id} sx={{ border: "1px solid #ddd" }}>
                          <CardContent sx={{ p: 2 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                mb: 1,
                              }}
                            >
                              <Box>
                                <Typography
                                  variant="h6"
                                  sx={{ fontSize: "1rem" }}
                                >
                                  {leader.name}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Team Lead
                                </Typography>
                              </Box>
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() =>
                                  handleDirectTeamLeadSelect(leader)
                                }
                              >
                                View Team
                              </Button>
                            </Box>
                            {leader.members && leader.members.length > 0 && (
                              <Box sx={{ mt: 1 }}>
                                <Typography
                                  variant="body2"
                                  sx={{ mb: 1, fontSize: "0.8rem" }}
                                >
                                  Team Members ({leader.members.length}):
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 0.5,
                                  }}
                                >
                                  {leader.members.slice(0, 4).map((m) => (
                                    <Chip
                                      key={m.id || m.userId}
                                      label={`${m.name} (${m.role.replace(
                                        "_",
                                        " "
                                      )})`}
                                      size="small"
                                      variant="outlined"
                                      sx={{
                                        fontSize: "0.7rem",
                                        height: "20px",
                                      }}
                                    />
                                  ))}
                                  {leader.members.length > 4 && (
                                    <Chip
                                      label={`+${
                                        leader.members.length - 4
                                      } more`}
                                      size="small"
                                      sx={{
                                        fontSize: "0.7rem",
                                        height: "20px",
                                      }}
                                    />
                                  )}
                                </Box>
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                  </Box>
                </Box>
              )}

              {branchMembers.some((m) => m.role === "relationship_manager") && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
                    Direct Relationship Managers
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={handleViewZonalDirectRMs}
                    size="small"
                    disabled={isFetchingDirectZonalRMs}
                  >
                    {isFetchingDirectZonalRMs
                      ? "Loading..."
                      : "View Direct RMs"}
                  </Button>
                  {viewingDirectZonalRMs && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: 1 }}
                    >
                      Calendar currently showing direct Relationship Managers.
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowBranchManagersModal(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showChartBranchManagersModal}
        onClose={() => setShowChartBranchManagersModal(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Button
              variant="text"
              size="small"
              onClick={() => setShowChartBranchManagersModal(false)}
              startIcon={<FiChevronLeft />}
              sx={{ minWidth: "auto", px: 1 }}
            >
              Back
            </Button>
            <Typography variant="h6">Teams under {userName}</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {branchMembers.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="body1">No teams found</Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {branchMembers.some((m) => m.role === "branch_manager") && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
                    Branch Manager Teams
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(300px, 1fr))",
                      gap: 2,
                    }}
                  >
                    {branchMembers
                      .filter((m) => m.role === "branch_manager")
                      .map((leader) => (
                        <Card key={leader.id} sx={{ border: "1px solid #ddd" }}>
                          <CardContent sx={{ p: 2 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                mb: 1,
                              }}
                            >
                              <Box>
                                <Typography
                                  variant="h6"
                                  sx={{ fontSize: "1rem" }}
                                >
                                  {leader.name}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Branch Manager
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 1,
                                }}
                              >
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={() =>
                                    handleChartMemberSelect(leader)
                                  }
                                >
                                  View Team
                                </Button>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() =>
                                    handleChartViewBranchManagerDirectRMs(
                                      leader
                                    )
                                  }
                                >
                                  View Direct RM
                                </Button>
                              </Box>
                            </Box>
                            {leader.members && leader.members.length > 0 && (
                              <Box sx={{ mt: 1 }}>
                                <Typography
                                  variant="body2"
                                  sx={{ mb: 1, fontSize: "0.8rem" }}
                                >
                                  Team Members ({leader.members.length}):
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 0.5,
                                  }}
                                >
                                  {(() => {
                                    // Remove duplicates from team members
                                    const uniqueMembers = leader.members.filter(
                                      (member, index, self) =>
                                        index ===
                                        self.findIndex(
                                          (m) =>
                                            m.userId === member.userId ||
                                            m.id === member.id
                                        )
                                    );
                                    return (
                                      <>
                                        {uniqueMembers.slice(0, 4).map((m) => (
                                          <Chip
                                            key={`${m.id || m.userId}-${
                                              m.name
                                            }`}
                                            label={`${m.name} (${m.role.replace(
                                              "_",
                                              " "
                                            )})`}
                                            size="small"
                                            variant="outlined"
                                            sx={{
                                              fontSize: "0.7rem",
                                              height: "20px",
                                            }}
                                          />
                                        ))}
                                        {uniqueMembers.length > 4 && (
                                          <Chip
                                            label={`+${
                                              uniqueMembers.length - 4
                                            } more`}
                                            size="small"
                                            sx={{
                                              fontSize: "0.7rem",
                                              height: "20px",
                                            }}
                                          />
                                        )}
                                      </>
                                    );
                                  })()}
                                </Box>
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                  </Box>
                </Box>
              )}

              {branchMembers.some((m) => m.role === "team_lead") && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
                    Direct Team Lead Teams
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(300px, 1fr))",
                      gap: 2,
                    }}
                  >
                    {branchMembers
                      .filter((m) => m.role === "team_lead")
                      .map((leader) => (
                        <Card key={leader.id} sx={{ border: "1px solid #ddd" }}>
                          <CardContent sx={{ p: 2 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                mb: 1,
                              }}
                            >
                              <Box>
                                <Typography
                                  variant="h6"
                                  sx={{ fontSize: "1rem" }}
                                >
                                  {leader.name}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Team Lead
                                </Typography>
                              </Box>
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => handleChartMemberSelect(leader)}
                              >
                                View Team
                              </Button>
                            </Box>
                            {leader.members && leader.members.length > 0 && (
                              <Box sx={{ mt: 1 }}>
                                <Typography
                                  variant="body2"
                                  sx={{ mb: 1, fontSize: "0.8rem" }}
                                >
                                  Team Members ({leader.members.length}):
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 0.5,
                                  }}
                                >
                                  {leader.members.slice(0, 4).map((m) => (
                                    <Chip
                                      key={m.id || m.userId}
                                      label={`${m.name} (${m.role.replace(
                                        "_",
                                        " "
                                      )})`}
                                      size="small"
                                      variant="outlined"
                                      sx={{
                                        fontSize: "0.7rem",
                                        height: "20px",
                                      }}
                                    />
                                  ))}
                                  {leader.members.length > 4 && (
                                    <Chip
                                      label={`+${
                                        leader.members.length - 4
                                      } more`}
                                      size="small"
                                      sx={{
                                        fontSize: "0.7rem",
                                        height: "20px",
                                      }}
                                    />
                                  )}
                                </Box>
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                  </Box>
                </Box>
              )}

              {branchMembers.some((m) => m.role === "relationship_manager") && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
                    Direct Relationship Managers
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={handleChartViewZonalDirectRMs}
                    size="small"
                    disabled={isFetchingChartDirectZonalRMs}
                  >
                    {isFetchingChartDirectZonalRMs
                      ? "Loading..."
                      : "View Direct RMs"}
                  </Button>
                  {chartViewingDirectZonalRMs && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: 1 }}
                    >
                      Charts currently showing direct Relationship Managers.
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowChartBranchManagersModal(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showCurrentLevelUsersModal}
        onClose={() => setShowCurrentLevelUsersModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Button
              variant="text"
              size="small"
              onClick={() => setShowCurrentLevelUsersModal(false)}
              startIcon={<FiChevronLeft />}
              sx={{ minWidth: "auto", px: 1 }}
            >
              Back
            </Button>
            <Typography variant="h6">
              {calendarLevel === "branch_manager" &&
                `Team Leads under ${selectedUserName}`}
              {calendarLevel === "team_lead" &&
                `Relationship Managers under ${selectedUserName}`}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
            {calendarLevel === "branch_manager" &&
              "Select a Team Lead to view their Relationship Managers and submission calendar."}
            {calendarLevel === "team_lead" &&
              "View Relationship Managers and their submission details."}
          </Typography>
          {currentLevelUsers.length > 0 ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Status</TableCell>
                    {calendarLevel === "branch_manager" && (
                      <TableCell>Actions</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentLevelUsers.map((member, index) => (
                    <TableRow
                      key={`level-${member.userId || member.id || index}`}
                    >
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {member.name || member.username}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {member.leaveStatus ? (
                          <Box sx={{ color: "#D97706", fontWeight: "bold" }}>
                            On Leave
                          </Box>
                        ) : (
                          <Box sx={{ color: "#16A34A", fontWeight: "bold" }}>
                            Active
                          </Box>
                        )}
                      </TableCell>
                      {calendarLevel === "branch_manager" && (
                        <TableCell>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              navigateToLevel(
                                "team_lead",
                                member.userId,
                                member.name || member.username,
                                {
                                  teamLeadRecordId:
                                    member.teamLeadRecordId ||
                                    member.teamLeadId ||
                                    member.team_lead_id ||
                                    member.id ||
                                    member.userId,
                                }
                              );
                              setShowCurrentLevelUsersModal(false);
                            }}
                          >
                            View RMs
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography color="textSecondary">
              {calendarLevel === "branch_manager" && "No Team Leads found."}
              {calendarLevel === "team_lead" &&
                "No Relationship Managers found."}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCurrentLevelUsersModal(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={showChartCurrentLevelUsersModal}
        onClose={() => setShowChartCurrentLevelUsersModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Button
              variant="text"
              size="small"
              onClick={() => setShowChartCurrentLevelUsersModal(false)}
              startIcon={<FiChevronLeft />}
              sx={{ minWidth: "auto", px: 1 }}
            >
              Back
            </Button>
            <Typography variant="h6">
              {chartLevel === "zonal" &&
                `Branch Managers under ${user.name || user.username}`}
              {chartLevel === "branch_manager" &&
                `Team Leads under ${chartSelectedUserName}`}
              {chartLevel === "team_lead" &&
                `Relationship Managers under ${chartSelectedUserName}`}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
            {chartLevel === "zonal" &&
              "Select a Branch Manager to view their Team Leads performance chart."}
            {chartLevel === "branch_manager" &&
              "Select a Team Lead to view their Relationship Managers performance chart."}
            {chartLevel === "team_lead" &&
              "View Relationship Managers performance details."}
          </Typography>
          {chartCurrentLevelUsers.length > 0 ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Status</TableCell>
                    {(chartLevel === "zonal" ||
                      chartLevel === "branch_manager") && (
                      <TableCell>Actions</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {chartCurrentLevelUsers.map((member, index) => (
                    <TableRow
                      key={`chart-level-${member.userId || member.id || index}`}
                    >
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {member.name || member.username}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {member.leaveStatus ? (
                          <Box sx={{ color: "#D97706", fontWeight: "bold" }}>
                            On Leave
                          </Box>
                        ) : (
                          <Box sx={{ color: "#16A34A", fontWeight: "bold" }}>
                            Active
                          </Box>
                        )}
                      </TableCell>
                      {(chartLevel === "zonal" ||
                        chartLevel === "branch_manager") && (
                        <TableCell>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              if (chartLevel === "zonal") {
                                navigateChartToLevel(
                                  "branch_manager",
                                  member.userId,
                                  member.name || member.username
                                );
                              } else if (chartLevel === "branch_manager") {
                                navigateChartToLevel(
                                  "team_lead",
                                  member.userId,
                                  member.name || member.username
                                );
                              }
                              setShowChartCurrentLevelUsersModal(false);
                            }}
                          >
                            {chartLevel === "zonal"
                              ? "View Team Leads"
                              : "View RMs"}
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography color="textSecondary">
              {chartLevel === "zonal" && "No Branch Managers found."}
              {chartLevel === "branch_manager" && "No Team Leads found."}
              {chartLevel === "team_lead" && "No Relationship Managers found."}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowChartCurrentLevelUsersModal(false)}>
            Close
          </Button>
        </DialogActions>{" "}
      </Dialog>

      <Dialog
        open={showTableBranchManagersModal}
        onClose={() => setShowTableBranchManagersModal(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Button
              variant="text"
              size="small"
              onClick={() => setShowTableBranchManagersModal(false)}
              startIcon={<FiChevronLeft />}
              sx={{ minWidth: "auto", px: 1 }}
            >
              Back
            </Button>
            <Typography variant="h6">Teams under {userName}</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {branchMembers.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="body1">No teams found</Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {branchMembers.some((m) => m.role === "branch_manager") && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
                    Branch Manager Teams
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(300px, 1fr))",
                      gap: 2,
                    }}
                  >
                    {branchMembers
                      .filter((m) => m.role === "branch_manager")
                      .map((leader) => (
                        <Card key={leader.id} sx={{ border: "1px solid #ddd" }}>
                          <CardContent sx={{ p: 2 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                mb: 1,
                              }}
                            >
                              <Box>
                                <Typography
                                  variant="h6"
                                  sx={{ fontSize: "1rem" }}
                                >
                                  {leader.name}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Branch Manager
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 1,
                                }}
                              >
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={() => {
                                    navigateTableToLevel(
                                      "branch_manager",
                                      leader.userId || leader.id,
                                      leader.name
                                    );
                                    setShowTableBranchManagersModal(false);
                                  }}
                                >
                                  View Team Leads
                                </Button>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => handleViewDirectRMs(leader)}
                                >
                                  View Direct RM
                                </Button>
                              </Box>
                            </Box>
                            {leader.members && leader.members.length > 0 && (
                              <Box sx={{ mt: 1 }}>
                                <Typography
                                  variant="body2"
                                  sx={{ mb: 1, fontSize: "0.8rem" }}
                                >
                                  Team Members ({leader.members.length}):
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 0.5,
                                  }}
                                >
                                  {(() => {
                                    // Remove duplicates from team members
                                    const uniqueMembers = leader.members.filter(
                                      (member, index, self) =>
                                        index ===
                                        self.findIndex(
                                          (m) =>
                                            m.userId === member.userId ||
                                            m.id === member.id
                                        )
                                    );
                                    return (
                                      <>
                                        {uniqueMembers.slice(0, 4).map((m) => (
                                          <Chip
                                            key={`${m.id || m.userId}-${
                                              m.name
                                            }`}
                                            label={`${m.name} (${m.role.replace(
                                              "_",
                                              " "
                                            )})`}
                                            size="small"
                                            variant="outlined"
                                            sx={{
                                              fontSize: "0.7rem",
                                              height: "20px",
                                            }}
                                          />
                                        ))}
                                        {uniqueMembers.length > 4 && (
                                          <Chip
                                            label={`+${
                                              uniqueMembers.length - 4
                                            } more`}
                                            size="small"
                                            sx={{
                                              fontSize: "0.7rem",
                                              height: "20px",
                                            }}
                                          />
                                        )}
                                      </>
                                    );
                                  })()}
                                </Box>
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                  </Box>
                </Box>
              )}

              {branchMembers.some((m) => m.role === "team_lead") && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
                    Direct Team Lead Teams
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(300px, 1fr))",
                      gap: 2,
                    }}
                  >
                    {branchMembers
                      .filter((m) => m.role === "team_lead")
                      .map((leader) => (
                        <Card key={leader.id} sx={{ border: "1px solid #ddd" }}>
                          <CardContent sx={{ p: 2 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                mb: 1,
                              }}
                            >
                              <Box>
                                <Typography
                                  variant="h6"
                                  sx={{ fontSize: "1rem" }}
                                >
                                  {leader.name}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Team Lead
                                </Typography>
                              </Box>
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => {
                                  navigateTableToLevel(
                                    "team_lead",
                                    leader.userId || leader.id,
                                    leader.name
                                  );
                                  setShowTableBranchManagersModal(false);
                                }}
                              >
                                View Relationship Managers
                              </Button>
                            </Box>
                            {leader.members && leader.members.length > 0 && (
                              <Box sx={{ mt: 1 }}>
                                <Typography
                                  variant="body2"
                                  sx={{ mb: 1, fontSize: "0.8rem" }}
                                >
                                  Team Members ({leader.members.length}):
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 0.5,
                                  }}
                                >
                                  {leader.members.slice(0, 4).map((m) => (
                                    <Chip
                                      key={m.id || m.userId}
                                      label={`${m.name} (${m.role.replace(
                                        "_",
                                        " "
                                      )})`}
                                      size="small"
                                      variant="outlined"
                                      sx={{
                                        fontSize: "0.7rem",
                                        height: "20px",
                                      }}
                                    />
                                  ))}
                                  {leader.members.length > 4 && (
                                    <Chip
                                      label={`+${
                                        leader.members.length - 4
                                      } more`}
                                      size="small"
                                      sx={{
                                        fontSize: "0.7rem",
                                        height: "20px",
                                      }}
                                    />
                                  )}
                                </Box>
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                  </Box>
                </Box>
              )}

              {branchMembers.some((m) => m.role === "relationship_manager") && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
                    Direct Relationship Managers
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(300px, 1fr))",
                      gap: 2,
                    }}
                  >
                    {branchMembers
                      .filter((m) => m.role === "relationship_manager")
                      .map((leader) => (
                        <Card key={leader.id} sx={{ border: "1px solid #ddd" }}>
                          <CardContent sx={{ p: 2 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                mb: 1,
                              }}
                            >
                              <Box>
                                <Typography
                                  variant="h6"
                                  sx={{ fontSize: "1rem" }}
                                >
                                  {leader.name}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Relationship Manager
                                </Typography>
                              </Box>
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => {
                                  navigateTableToLevel(
                                    "relationship_manager",
                                    leader.userId || leader.id,
                                    leader.name
                                  );
                                  setShowTableBranchManagersModal(false);
                                }}
                              >
                                View Details
                              </Button>
                            </Box>
                          </CardContent>
                        </Card>
                      ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTableBranchManagersModal(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={showDirectRMsModal}
        onClose={() => setShowDirectRMsModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">
            Direct Relationship Managers under {directRMManagerName}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {directRMsForModal.length > 0 ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {directRMsForModal.map((rm) => (
                <Card
                  key={rm.userId || rm.id || rm.rmId || rm.name}
                  sx={{ border: "1px solid #ddd" }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {rm.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Relationship Manager
                    </Typography>
                    {typeof rm.aggregatedPoints === "number" && (
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        Total Points: {rm.aggregatedPoints}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <Typography color="textSecondary">
              No Direct Relationship Managers found for this Branch Manager.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDirectRMsModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ZonalHeadDashboard;
